import React, { useState, useEffect, useRef, useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import * as XLSX from "xlsx";
import { saleApi, clientApi, inventoryApi } from "../services/api";
import { useAuth } from "../context/AuthContext";
import {
  FiPlus,
  FiTrash2,
  FiEdit,
  FiSearch,
  FiCheckCircle,
  FiUsers,
  FiBox,
  FiList,
  FiChevronDown,
  FiRotateCcw,
  FiTag,
  FiFileText,
  FiClock,
  FiEye,
  FiCalendar,
  FiLayers,
  FiChevronLeft,
  FiChevronRight,
  FiX,
  FiDollarSign,
  FiShoppingBag,
  FiUpload,
  FiDownload,
  FiAlertTriangle,
  FiRefreshCw,
} from "react-icons/fi";
import { toast } from "react-toastify";
import Modal from "../components/Modal";
import SaleReturnsTab from "../components/SaleReturnsTab";

// Searchable Dropdown for Selecting Client / Customer
const SearchableClientSelect = ({
  clients,
  selectedClientId,
  onSelect,
  placeholder = "-- Choose or search client customer --",
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const dropdownRef = useRef(null);
  const searchInputRef = useRef(null);

  const selectedClient = clients.find((c) => String(c.id) === String(selectedClientId));

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      setTimeout(() => {
        searchInputRef.current?.focus();
      }, 50);
    }
  }, [isOpen]);

  const filteredClients = clients.filter((c) => {
    if (!searchTerm.trim()) return true;
    const term = searchTerm.toLowerCase();
    return (
      (c.company_name && c.company_name.toLowerCase().includes(term)) ||
      (c.contact_person_name && c.contact_person_name.toLowerCase().includes(term)) ||
      (c.contact && c.contact.toLowerCase().includes(term)) ||
      (c.email && c.email.toLowerCase().includes(term)) ||
      (c.ntn && c.ntn.toLowerCase().includes(term)) ||
      (c.city && c.city.toLowerCase().includes(term)) ||
      (c.address && c.address.toLowerCase().includes(term))
    );
  });

  return (
    <div ref={dropdownRef} style={{ position: "relative", width: "100%" }}>
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        style={{
          width: "100%",
          height: 42,
          padding: "9px 14px",
          borderRadius: 8,
          border: selectedClient ? "1.5px solid var(--primary, #064E3B)" : "1.5px solid var(--slate-300)",
          backgroundColor: "#fff",
          textAlign: "left",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          cursor: "pointer",
          fontSize: "0.86rem",
          fontWeight: selectedClient ? 600 : 400,
          color: selectedClient ? "var(--navy)" : "var(--slate-400)",
          boxShadow: "0 1px 2px rgba(0,0,0,0.03)",
          boxSizing: "border-box",
          transition: "border-color 0.15s ease",
        }}
      >
        <div style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", marginRight: 8, display: "flex", alignItems: "center", gap: 8 }}>
          <FiUsers size={16} style={{ color: selectedClient ? "var(--primary)" : "var(--slate-400)", flexShrink: 0 }} />
          {selectedClient ? (
            <div style={{ display: "flex", alignItems: "center", gap: 8, overflow: "hidden", textOverflow: "ellipsis" }}>
              <span style={{ fontWeight: 700, color: "var(--navy)" }}>{selectedClient.company_name}</span>
              {selectedClient.contact_person_name && (
                <span style={{ fontSize: "0.76rem", color: "var(--slate-500)", fontWeight: 500 }}>
                  ({selectedClient.contact_person_name})
                </span>
              )}
              {selectedClient.contact && (
                <span
                  style={{
                    fontSize: "0.72rem",
                    backgroundColor: "var(--slate-100)",
                    color: "var(--slate-600)",
                    padding: "1px 6px",
                    borderRadius: 4,
                    fontWeight: 600,
                  }}
                >
                  {selectedClient.contact}
                </span>
              )}
            </div>
          ) : (
            placeholder
          )}
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 6, flexShrink: 0 }}>
          {selectedClient && (
            <span
              onClick={(e) => {
                e.stopPropagation();
                onSelect("");
              }}
              style={{
                cursor: "pointer",
                padding: "2px 6px",
                color: "var(--slate-400)",
                fontSize: "0.9rem",
                borderRadius: 4,
                lineHeight: 1,
              }}
              title="Clear selection"
            >
              ×
            </span>
          )}
          <FiChevronDown
            size={16}
            style={{
              color: "var(--slate-400)",
              transform: isOpen ? "rotate(180deg)" : "rotate(0deg)",
              transition: "transform 0.15s ease",
            }}
          />
        </div>
      </button>

      {isOpen && (
        <div
          style={{
            position: "absolute",
            top: "100%",
            left: 0,
            right: 0,
            zIndex: 1050,
            backgroundColor: "#fff",
            borderRadius: 8,
            border: "1px solid var(--slate-300)",
            boxShadow: "0 10px 25px -5px rgba(0, 0, 0, 0.15), 0 8px 10px -6px rgba(0, 0, 0, 0.1)",
            marginTop: 4,
            maxHeight: 290,
            overflowY: "auto",
          }}
        >
          {/* Search box inside dropdown */}
          <div
            style={{
              padding: "8px 10px",
              borderBottom: "1px solid var(--slate-200)",
              backgroundColor: "var(--slate-50)",
              position: "sticky",
              top: 0,
              zIndex: 2,
            }}
          >
            <div style={{ position: "relative" }}>
              <FiSearch
                size={14}
                style={{
                  position: "absolute",
                  left: 10,
                  top: "50%",
                  transform: "translateY(-50%)",
                  color: "var(--slate-400)",
                }}
              />
              <input
                ref={searchInputRef}
                type="text"
                placeholder="Search by client name, person, phone, NTN, city..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onClick={(e) => e.stopPropagation()}
                style={{
                  width: "100%",
                  padding: "7px 10px 7px 30px",
                  borderRadius: 6,
                  border: "1.5px solid var(--primary-light, #BBF7D0)",
                  fontSize: "0.82rem",
                  outline: "none",
                  boxSizing: "border-box",
                }}
              />
            </div>
          </div>

          {/* List Options */}
          {filteredClients.length === 0 ? (
            <div style={{ padding: "16px 12px", textAlign: "center", color: "var(--slate-400)", fontSize: "0.82rem" }}>
              {clients.length === 0 ? "No clients found in system." : `No client found matching "${searchTerm}"`}
            </div>
          ) : (
            filteredClients.map((c) => {
              const isSelected = String(c.id) === String(selectedClientId);
              return (
                <div
                  key={c.id}
                  onClick={() => {
                    onSelect(c.id);
                    setIsOpen(false);
                    setSearchTerm("");
                  }}
                  style={{
                    padding: "9px 12px",
                    borderBottom: "1px solid var(--slate-100)",
                    cursor: "pointer",
                    backgroundColor: isSelected ? "#F0FDF4" : "transparent",
                    transition: "background-color 0.1s ease",
                  }}
                  onMouseEnter={(e) => {
                    if (!isSelected) e.currentTarget.style.backgroundColor = "var(--slate-50)";
                  }}
                  onMouseLeave={(e) => {
                    if (!isSelected) e.currentTarget.style.backgroundColor = "transparent";
                  }}
                >
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <div>
                      <div style={{ fontWeight: 700, color: "var(--navy)", fontSize: "0.86rem" }}>
                        {c.company_name}
                      </div>
                      <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 2, flexWrap: "wrap" }}>
                        {c.contact_person_name && (
                          <span style={{ fontSize: "0.75rem", color: "var(--slate-600)" }}>
                            Rep: {c.contact_person_name}
                          </span>
                        )}
                        {c.contact && (
                          <span style={{ fontSize: "0.72rem", color: "var(--slate-500)", fontFamily: "monospace" }}>
                            {c.contact}
                          </span>
                        )}
                        {c.city && (
                          <span style={{ fontSize: "0.72rem", color: "var(--slate-400)" }}>
                            • {c.city}
                          </span>
                        )}
                      </div>
                    </div>
                    {isSelected && (
                      <span style={{ color: "var(--primary)", fontWeight: 700, fontSize: "0.78rem" }}>
                        ✓
                      </span>
                    )}
                  </div>
                </div>
              );
            })
          )}
        </div>
      )}
    </div>
  );
};

// Searchable Dropdown for Selecting Available Inventory Items to Sell
const SearchableItemSelect = ({
  items,
  selectedItemId,
  onSelect,
  placeholder = "Search available items by name, SKU, serial #...",
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const dropdownRef = useRef(null);
  const searchInputRef = useRef(null);

  const selectedItem = items.find((it) => String(it.id) === String(selectedItemId));

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      setTimeout(() => {
        searchInputRef.current?.focus();
      }, 50);
    }
  }, [isOpen]);

  const filteredItems = items.filter((it) => {
    if (!searchTerm.trim()) return true;
    const term = searchTerm.toLowerCase();
    return (
      (it.item_name && it.item_name.toLowerCase().includes(term)) ||
      (it.sku && it.sku.toLowerCase().includes(term)) ||
      (it.serial_number && it.serial_number.toLowerCase().includes(term)) ||
      (it.part_number && it.part_number.toLowerCase().includes(term)) ||
      (it.category_name && it.category_name.toLowerCase().includes(term)) ||
      (it.subcategory_name && it.subcategory_name.toLowerCase().includes(term))
    );
  });

  return (
    <div ref={dropdownRef} style={{ position: "relative", width: "100%" }}>
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        style={{
          width: "100%",
          height: 42,
          padding: "9px 14px",
          borderRadius: 8,
          border: selectedItem ? "1.5px solid var(--primary, #064E3B)" : "1.5px solid var(--slate-300)",
          backgroundColor: "#fff",
          textAlign: "left",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          cursor: "pointer",
          fontSize: "0.86rem",
          fontWeight: selectedItem ? 600 : 400,
          color: selectedItem ? "var(--navy)" : "var(--slate-400)",
          boxShadow: "0 1px 2px rgba(0,0,0,0.03)",
          boxSizing: "border-box",
          transition: "border-color 0.15s ease",
        }}
      >
        <div style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", marginRight: 8 }}>
          {selectedItem ? (
            <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
              <span style={{ fontWeight: 800, color: "var(--navy)" }}>{selectedItem.item_name}</span>
              {selectedItem.sku && (
                <span
                  style={{
                    fontSize: "0.72rem",
                    backgroundColor: "var(--primary-light, #ECFDF5)",
                    color: "var(--primary, #064E3B)",
                    border: "1px solid rgba(6, 78, 59, 0.2)",
                    padding: "1px 6px",
                    borderRadius: 4,
                    fontWeight: 700,
                    fontFamily: "monospace",
                  }}
                >
                  {selectedItem.sku}
                </span>
              )}
              {selectedItem.serial_number && (
                <span style={{ fontSize: "0.72rem", color: "var(--slate-500)", fontFamily: "monospace" }}>
                  SN: {selectedItem.serial_number}
                </span>
              )}
            </div>
          ) : (
            placeholder
          )}
        </div>
        <FiChevronDown
          size={15}
          style={{
            color: "var(--slate-400)",
            transform: isOpen ? "rotate(180deg)" : "rotate(0deg)",
            transition: "transform 0.15s ease",
            flexShrink: 0,
          }}
        />
      </button>

      {isOpen && (
        <div
          style={{
            position: "absolute",
            top: "100%",
            left: 0,
            right: 0,
            zIndex: 1050,
            backgroundColor: "#fff",
            borderRadius: 8,
            border: "1px solid var(--slate-300)",
            boxShadow: "0 10px 25px -5px rgba(0, 0, 0, 0.15), 0 8px 10px -6px rgba(0, 0, 0, 0.1)",
            marginTop: 4,
            maxHeight: 280,
            overflowY: "auto",
          }}
        >
          {/* Search box inside dropdown */}
          <div
            style={{
              padding: "8px 10px",
              borderBottom: "1px solid var(--slate-200)",
              backgroundColor: "var(--slate-50)",
              position: "sticky",
              top: 0,
              zIndex: 2,
            }}
          >
            <div style={{ position: "relative" }}>
              <FiSearch
                size={14}
                style={{
                  position: "absolute",
                  left: 10,
                  top: "50%",
                  transform: "translateY(-50%)",
                  color: "var(--slate-400)",
                }}
              />
              <input
                ref={searchInputRef}
                type="text"
                placeholder="Search available inventory items..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onClick={(e) => e.stopPropagation()}
                style={{
                  width: "100%",
                  padding: "7px 10px 7px 30px",
                  borderRadius: 6,
                  border: "1px solid var(--slate-300)",
                  fontSize: "0.82rem",
                  outline: "none",
                  boxSizing: "border-box",
                }}
              />
            </div>
          </div>

          {/* List Options */}
          {filteredItems.length === 0 ? (
            <div style={{ padding: "16px 12px", textAlign: "center", color: "var(--slate-400)", fontSize: "0.82rem" }}>
              {items.length === 0 ? "No available inventory items found." : `No items found matching "${searchTerm}"`}
            </div>
          ) : (
            filteredItems.map((it) => {
              const isSelected = String(it.id) === String(selectedItemId);

              return (
                <div
                  key={it.id}
                  onClick={() => {
                    onSelect(it);
                    setIsOpen(false);
                    setSearchTerm("");
                  }}
                  style={{
                    padding: "9px 12px",
                    borderBottom: "1px solid var(--slate-100)",
                    cursor: "pointer",
                    backgroundColor: isSelected ? "var(--primary-light, #ECFDF5)" : "transparent",
                    transition: "background-color 0.1s ease",
                  }}
                  onMouseEnter={(e) => {
                    if (!isSelected) e.currentTarget.style.backgroundColor = "var(--slate-50)";
                  }}
                  onMouseLeave={(e) => {
                    if (!isSelected) e.currentTarget.style.backgroundColor = "transparent";
                  }}
                >
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 8 }}>
                    <div>
                      <div style={{ fontWeight: 700, color: "var(--navy)", fontSize: "0.85rem" }}>
                        {it.item_name}
                      </div>
                      <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 3, flexWrap: "wrap" }}>
                        <span
                          style={{
                            fontFamily: "monospace",
                            fontWeight: 700,
                            fontSize: "0.72rem",
                            backgroundColor: "var(--slate-100)",
                            color: "var(--primary, #064E3B)",
                            padding: "1px 6px",
                            borderRadius: 4,
                          }}
                        >
                          {it.sku}
                        </span>
                        {it.category_name && (
                          <span style={{ fontSize: "0.72rem", color: "var(--slate-500)" }}>
                            Category: <strong>{it.category_name}</strong>
                          </span>
                        )}
                        {it.serial_number && (
                          <span style={{ fontSize: "0.72rem", color: "var(--slate-500)", fontFamily: "monospace" }}>
                            SN: {it.serial_number}
                          </span>
                        )}
                        {it.part_number && (
                          <span style={{ fontSize: "0.72rem", color: "var(--slate-500)", fontFamily: "monospace" }}>
                            PN: {it.part_number}
                          </span>
                        )}
                      </div>
                    </div>

                    <div style={{ textAlign: "right", flexShrink: 0 }}>
                      <span
                        style={{
                          fontSize: "0.7rem",
                          fontWeight: 700,
                          padding: "2px 6px",
                          borderRadius: 4,
                          backgroundColor: "#DCFCE7",
                          color: "#15803D",
                        }}
                      >
                        Available
                      </span>
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>
      )}
    </div>
  );
};

const SalesPage = () => {
  const { user, isAdmin } = useAuth();
  const canEdit = isAdmin || (Array.isArray(user?.permissions) && user.permissions.includes("sales"));
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const fileInputRef = useRef(null);

  const [sales, setSales] = useState([]);
  const [clients, setClients] = useState([]);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  // Active View Tab: 'record' (Record Sale) | 'history' (Sale History) | 'returns' (Sale Returns)
  const [activeTab, setActiveTab] = useState(
    searchParams.get("tab") === "history"
      ? "history"
      : searchParams.get("tab") === "returns"
      ? "returns"
      : "record"
  );

  useEffect(() => {
    const tab = searchParams.get("tab");
    if (tab && (tab === "history" || tab === "record" || tab === "returns")) {
      setActiveTab(tab);
    }
  }, [searchParams]);

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setSearchParams(tab === "history" || tab === "returns" ? { tab } : {});
  };

  // Step 1: Client & Sale Details (client is NOT preselected)
  const [selectedClientId, setSelectedClientId] = useState("");
  const [saleDate, setSaleDate] = useState(new Date().toISOString().split("T")[0]);
  const [invoiceNumber, setInvoiceNumber] = useState("");

  // Step 2: Multi-row Sale Items (each row is 1 asset item with unit price)
  const createInitialRow = () => ({
    rowId: `sale-row-${Date.now()}-${Math.floor(Math.random() * 1000)}`,
    item_id: "",
    item_name: "",
    sku: "",
    serial_number: "",
    part_number: "",
    category_name: "",
    subcategory_name: "",
    current_stock: 1,
    quantity: 1,
    unit_price: "",
  });

  const [saleRows, setSaleRows] = useState([createInitialRow()]);
  const [submitting, setSubmitting] = useState(false);
  const [deleteModal, setDeleteModal] = useState({ open: false, sale: null });

  // Edit Sale State (for editing on the main Record tab)
  const [editingSale, setEditingSale] = useState(null);

  // Sale History: Search, Pagination
  const [historyInvoices, setHistoryInvoices] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historySearch, setHistorySearch] = useState("");
  const [debouncedHistorySearch, setDebouncedHistorySearch] = useState("");
  const [historyPage, setHistoryPage] = useState(1);
  const [historyPageSize, setHistoryPageSize] = useState(10);
  const [historyPagination, setHistoryPagination] = useState({
    total: 0,
    page: 1,
    limit: 10,
    totalPages: 1,
  });

  const populateSaleForEdit = (saleData) => {
    if (!saleData) return;
    setSelectedClientId(saleData.client_id ? String(saleData.client_id) : "");
    setSaleDate(
      saleData.sale_date
        ? String(saleData.sale_date).slice(0, 10)
        : saleData.created_at
        ? String(saleData.created_at).slice(0, 10)
        : new Date().toISOString().split("T")[0]
    );
    setInvoiceNumber(saleData.invoice_number || "");

    const rawItems = saleData.items || [];
    if (rawItems.length > 0) {
      setSaleRows(
        rawItems.map((it, idx) => {
          const sPrice =
            it.sale_price !== undefined && it.sale_price !== null
              ? it.sale_price
              : it.unit_price !== undefined && it.unit_price !== null
              ? it.unit_price
              : it.price || "";
          return {
            rowId: `sale-row-edit-${it.id || idx}-${Date.now()}`,
            item_id: it.id,
            item_name: it.item_name || "",
            sku: it.sku || "",
            serial_number: it.serial_number || "",
            part_number: it.part_number || "",
            category_name: it.category_name || "",
            subcategory_name: it.subcategory_name || "",
            current_stock: Math.max(1, parseInt(it.quantity) || 1),
            quantity: Math.max(1, parseInt(it.quantity) || 1),
            unit_price: sPrice,
          };
        })
      );
    }

    setEditingSale({
      id: saleData.id,
      originalInvoiceNumber: saleData.invoice_number,
    });
    setActiveTab("record");
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const loadSaleForEdit = async (invIdOrNo, saleObj = null) => {
    try {
      let fullSale = saleObj;
      if (!fullSale || !Array.isArray(fullSale.items) || fullSale.items.length === 0) {
        const res = await saleApi.getById(invIdOrNo);
        fullSale = res?.data || res;
      }
      if (fullSale) {
        populateSaleForEdit(fullSale);
      }
    } catch (err) {
      console.error("Error loading sale invoice for edit:", err);
      toast.error("Could not load sale invoice for editing.");
    }
  };

  const handleCancelEdit = () => {
    handleResetForm();
    setSearchParams({ tab: "record" });
    toast.info("Edit mode cancelled. Form reset.");
  };

  const fetchData = async (forceSkipEdit = false) => {
    try {
      setLoading(true);
      const [salesRes, clientsRes, itemsRes] = await Promise.all([
        saleApi.getAll(),
        clientApi.getAll(),
        inventoryApi.getAll(),
      ]);
      const fetchedSales = salesRes.data || [];
      setSales(fetchedSales);
      setClients(clientsRes.data || []);
      setItems(itemsRes.data || []);

      // Check if URL has editInvoice or editId
      const editInvoiceParam = !forceSkipEdit
        ? (searchParams.get("editInvoice") || searchParams.get("editId"))
        : null;
      if (editInvoiceParam) {
        const found = fetchedSales.find(
          (s) =>
            String(s.invoice_number).trim().toLowerCase() === String(editInvoiceParam).trim().toLowerCase() ||
            String(s.id) === String(editInvoiceParam)
        );
        loadSaleForEdit(editInvoiceParam, found);
      }
    } catch (err) {
      toast.error(err.message || "Failed to load sales information");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  // Format date display
  const formatDateDisplay = (dateStr) => {
    if (!dateStr) return "—";
    try {
      if (typeof dateStr === "string" && /^\d{4}-\d{2}-\d{2}/.test(dateStr)) {
        const parts = dateStr.slice(0, 10).split("-");
        const d = new Date(parseInt(parts[0], 10), parseInt(parts[1], 10) - 1, parseInt(parts[2], 10));
        return d.toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" });
      }
      const d = new Date(dateStr);
      if (isNaN(d.getTime())) return dateStr;
      return d.toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" });
    } catch {
      return dateStr;
    }
  };

  // Only items in inventory that are NOT sold and have stock > 0
  const availableItems = useMemo(() => {
    return items.filter((it) => {
      const isSold = it.is_sale === 1 || it.is_sale === true || it.is_sale === "1";
      const inStock = (parseInt(it.quantity) || 1) > 0;
      return !isSold && inStock;
    });
  }, [items]);

  // Already selected item IDs in other rows
  const selectedItemIds = useMemo(() => {
    return saleRows.map((r) => String(r.item_id)).filter(Boolean);
  }, [saleRows]);

  // Handle Item selection in a specific row
  const handleItemSelectInRow = (rowIndex, selectedItem) => {
    setSaleRows((prev) => {
      const updated = [...prev];
      if (!selectedItem) {
        updated[rowIndex] = {
          ...updated[rowIndex],
          item_id: "",
          item_name: "",
          sku: "",
          serial_number: "",
          part_number: "",
          category_name: "",
          subcategory_name: "",
          current_stock: 0,
          quantity: 1,
          unit_price: "",
        };
      } else {
        const stockQty = parseInt(selectedItem.quantity) || 1;
        updated[rowIndex] = {
          ...updated[rowIndex],
          item_id: selectedItem.id,
          item_name: selectedItem.item_name || "",
          sku: selectedItem.sku || "",
          serial_number: selectedItem.serial_number || "",
          part_number: selectedItem.part_number || "",
          category_name: selectedItem.category_name || "",
          subcategory_name: selectedItem.subcategory_name || "",
          current_stock: stockQty,
          quantity: 1,
          unit_price: "", // user enters sale_price manually
        };
      }
      return updated;
    });
  };

  // Handle Quantity update on row
  const handleRowQuantityChange = (rowIndex, value) => {
    setSaleRows((prev) => {
      const updated = [...prev];
      const maxStock = updated[rowIndex].current_stock || 1;
      let val = parseInt(value);
      if (isNaN(val) || val < 1) val = 1;
      if (val > maxStock) {
        toast.warning(`Maximum available stock for this item is ${maxStock} units.`);
        val = maxStock;
      }
      updated[rowIndex] = { ...updated[rowIndex], quantity: val };
      return updated;
    });
  };

  // Download Sample Excel Template for Bulk Sale Upload (Multi-sheet with Categories & Available Stock Reference)
  const handleDownloadSampleFile = () => {
    try {
      const sampleRows = [];
      const stockDesktops = availableItems.filter((it) => (it.category_name || "").toLowerCase().includes("desktop"));
      const stockLEDs = availableItems.filter((it) => (it.category_name || "").toLowerCase().includes("led"));
      const stockNVME = availableItems.filter((it) => (it.category_name || "").toLowerCase().includes("nvme"));
      const stockRAM = availableItems.filter((it) => (it.category_name || "").toLowerCase().includes("ram"));
      const stockKeyboard = availableItems.filter((it) => (it.category_name || "").toLowerCase().includes("keyboard"));
      const stockMouse = availableItems.filter((it) => (it.category_name || "").toLowerCase().includes("mouse") && !(it.category_name || "").toLowerCase().includes("pad"));
      const stockMousePad = availableItems.filter((it) => (it.category_name || "").toLowerCase().includes("mouse pad") || (it.category_name || "").toLowerCase().includes("pad"));
      const stockBag = availableItems.filter((it) => (it.category_name || "").toLowerCase().includes("bag"));
      const stockWin = availableItems.filter((it) => (it.category_name || "").toLowerCase().includes("windows"));
      const stockOff = availableItems.filter((it) => (it.category_name || "").toLowerCase().includes("office"));

      const dItem = stockDesktops[0];
      const lItem = stockLEDs[0];
      const nvmeItem = stockNVME[0];
      const ramItem = stockRAM[0];
      const kbItem = stockKeyboard[0];
      const mouseItem = stockMouse[0];
      const padItem = stockMousePad[0];
      const bagItem = stockBag[0];
      const winItem = stockWin[0];
      const offItem = stockOff[0];

      sampleRows.push([
        dItem?.serial_number || dItem?.sku || "Desktop",
        "Desktop",
        dItem?.item_name || "HP EliteDesk 800 G4 Desktop",
        "1",
        dItem?.sale_price || dItem?.price || "45000",
        dItem?.serial_number || "",
        dItem?.sku || "",
      ]);

      sampleRows.push([
        lItem?.serial_number || lItem?.sku || "LED",
        "LED",
        lItem?.item_name || "HP 24f IPS LED Display",
        "1",
        lItem?.sale_price || lItem?.price || "22000",
        lItem?.serial_number || "",
        lItem?.sku || "",
      ]);

      sampleRows.push([
        "NVME - GB",
        "NVME - GB",
        nvmeItem?.item_name || "Kingston NV2 512GB NVMe SSD",
        String(Math.min(5, parseInt(nvmeItem?.quantity) || 5)),
        nvmeItem?.sale_price || nvmeItem?.price || "10500",
        "",
        "",
      ]);

      sampleRows.push([
        "RAM - GB",
        "RAM - GB",
        ramItem?.item_name || "Samsung 16GB DDR4 3200MHz RAM",
        String(Math.min(5, parseInt(ramItem?.quantity) || 5)),
        ramItem?.sale_price || ramItem?.price || "7500",
        "",
        "",
      ]);

      if (kbItem) {
        sampleRows.push([
          "Keyboard",
          "Keyboard",
          kbItem.item_name || "Dell KB216 Wired Keyboard",
          String(Math.min(5, parseInt(kbItem.quantity) || 5)),
          kbItem.sale_price || kbItem.price || "2600",
          "",
          "",
        ]);
      }

      sampleRows.push([
        "Mouse",
        "Mouse",
        mouseItem?.item_name || "Logitech B100 USB Optical Mouse",
        String(Math.min(5, parseInt(mouseItem?.quantity) || 5)),
        mouseItem?.sale_price || mouseItem?.price || "1500",
        "",
        "",
      ]);

      sampleRows.push([
        "Mouse Pad",
        "Mouse Pad",
        padItem?.item_name || "Standard Gaming Mouse Pad",
        String(Math.min(10, parseInt(padItem?.quantity) || 10)),
        padItem?.sale_price || padItem?.price || "600",
        "",
        "",
      ]);

      sampleRows.push([
        "Laptop Bag",
        "Laptop Bag",
        bagItem?.item_name || "Targus 15.6 Laptop Backpack",
        String(Math.min(5, parseInt(bagItem?.quantity) || 5)),
        bagItem?.sale_price || bagItem?.price || "4500",
        "",
        "",
      ]);

      sampleRows.push([
        "Windows License",
        "Windows License",
        winItem?.item_name || "Windows 11 Pro OEM Digital License",
        String(Math.min(2, parseInt(winItem?.quantity) || 2)),
        winItem?.sale_price || winItem?.price || "18000",
        "",
        "",
      ]);

      sampleRows.push([
        "Office License",
        "Office License",
        offItem?.item_name || "Microsoft Office 2021 Home & Business",
        String(Math.min(2, parseInt(offItem?.quantity) || 2)),
        offItem?.sale_price || offItem?.price || "29000",
        "",
        "",
      ]);

      const sampleData = [
        [
          "Identifier (Serial # / SKU / Item Name)*",
          "Category (Optional)",
          "Item Name (Optional)",
          "Count / Quantity*",
          "Sale Price (Rs.)*",
          "Serial Number (Hardware Only)",
          "SKU Number (Optional)",
        ],
        ...sampleRows,
      ];

      const ws = XLSX.utils.aoa_to_sheet(sampleData);
      ws["!cols"] = [
        { wch: 30 },
        { wch: 18 },
        { wch: 32 },
        { wch: 16 },
        { wch: 18 },
        { wch: 26 },
        { wch: 20 },
      ];

      // Second sheet: Available Stock Reference
      const refStockData = [
        ["Item Name", "Category", "In-Stock Quantity", "Serial Number", "SKU Number", "Purchase Unit Cost"],
        ...availableItems.map((it) => [
          it.item_name || "—",
          it.category_name || "—",
          parseInt(it.quantity) || 1,
          it.serial_number || "— (Bulk)",
          it.sku || "—",
          parseFloat(it.price) || 0,
        ]),
      ];
      const wsRef = XLSX.utils.aoa_to_sheet(refStockData);
      wsRef["!cols"] = [
        { wch: 32 },
        { wch: 20 },
        { wch: 18 },
        { wch: 26 },
        { wch: 20 },
        { wch: 20 },
      ];

      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Sales Upload");
      XLSX.utils.book_append_sheet(wb, wsRef, "Available Stock Reference");
      XLSX.writeFile(wb, "sales_upload_template.xlsx");
      toast.success("Sales Upload Template with In-Stock Reference downloaded!");
    } catch (err) {
      console.error("Download sample error:", err);
      toast.error("Failed to generate sample template file.");
    }
  };

  // Bulk Upload File Handler: Reads Serial Number, SKU, Item Name, Category, Count, and Price
  // Bulk Upload File Handler: Reads Serial Number, SKU, Item Name, Category, Count, and Price
  const handleFileUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    console.log("%c[SALE SHEET UPLOAD] Selected file:", "color: #2563EB; font-weight: bold; font-size: 13px;", {
      name: file.name,
      size: `${(file.size / 1024).toFixed(2)} KB`,
      type: file.type,
      lastModified: new Date(file.lastModified).toISOString(),
    });

    const reader = new FileReader();
    reader.onerror = (readErr) => {
      console.error("%c[SALE SHEET UPLOAD ERROR] FileReader error:", "color: #DC2626; font-weight: bold;", readErr);
      toast.error(`FileReader error: ${readErr.message || "Failed to read file"}`);
    };
    reader.onload = (evt) => {
      try {
        const data = new Uint8Array(evt.target.result);
        const workbook = XLSX.read(data, { type: "array" });
        console.log("%c[SALE SHEET UPLOAD] Workbook parsed successfully:", "color: #059669; font-weight: bold;", {
          sheetNames: workbook.SheetNames,
        });

        const firstSheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[firstSheetName];
        const rawJson = XLSX.utils.sheet_to_json(worksheet, { defval: "" });

        console.log("%c[SALE SHEET UPLOAD] Raw rows read from Excel:", "color: #2563EB; font-weight: bold;", rawJson.length, rawJson);

        if (!rawJson || rawJson.length === 0) {
          console.warn("[SALE SHEET UPLOAD] The uploaded sheet is completely empty.");
          toast.warning("The uploaded sheet is empty.");
          return;
        }

        // Filter out completely empty rows where every cell is blank / whitespace / undefined
        const contentRows = rawJson.filter((row) => {
          if (!row || typeof row !== "object") return false;
          return Object.values(row).some((val) => {
            if (val === null || val === undefined) return false;
            const str = String(val).trim();
            return str !== "" && str !== "undefined" && str !== "null";
          });
        });

        console.log("%c[SALE SHEET UPLOAD] Rows with content count:", "color: #2563EB; font-weight: bold;", contentRows.length, `(ignored ${rawJson.length - contentRows.length} trailing/empty rows)`);

        // Auto-detect invoice number if present
        const rowWithInvoice = contentRows.find((row) => {
          return Object.keys(row).some((k) => {
            const clean = k.toLowerCase().replace(/[^a-z0-9]/g, "");
            return (
              (clean.includes("invoiceno") ||
                clean.includes("invoicenumber") ||
                clean === "invoice") &&
              String(row[k]).trim() !== ""
            );
          });
        });
        if (rowWithInvoice) {
          const invKey = Object.keys(rowWithInvoice).find((k) => {
            const clean = k.toLowerCase().replace(/[^a-z0-9]/g, "");
            return clean.includes("invoiceno") || clean.includes("invoicenumber") || clean === "invoice";
          });
          if (invKey && rowWithInvoice[invKey]) {
            const detectedInvoice = String(rowWithInvoice[invKey]).trim();
            console.log("[SALE SHEET UPLOAD] Auto-detected invoice number:", detectedInvoice);
            setInvoiceNumber(detectedInvoice);
          }
        }

        const newRows = [];
        const notFoundItems = [];
        const alreadySoldItems = [];
        const stockExceededItems = [];

        // Helper string normalizer
        const cleanStr = (s) => String(s || "").toLowerCase().replace(/[^a-z0-9]/g, "");
        const usedItemIds = new Set();

        contentRows.forEach((row, idx) => {
          const normalized = {};
          Object.keys(row).forEach((k) => {
            const cleanKey = k.toLowerCase().replace(/[^a-z0-9]/g, "");
            normalized[cleanKey] = row[k];
          });

          // Accurate Serial Number extractor (avoids composite identifier or name columns)
          const getSerialVal = () => {
            const keys = Object.keys(normalized);
            // 1. Exact match for dedicated serial headers
            for (const k of keys) {
              if (["serialnumberhardwareonly", "serialnumber", "serialno", "serialnum", "serial", "hardwareserial", "sn"].includes(k)) {
                const val = String(normalized[k] || "").trim();
                if (val && val !== "undefined" && val !== "null") return val;
              }
            }
            // 2. Substring match for serial headers (ignoring composite identifier/part/sku/name columns)
            for (const k of keys) {
              if (k.includes("identifier") || k.includes("sku") || k.includes("part") || k.includes("name") || k.includes("desc") || k.includes("model")) continue;
              if (k.includes("serial") || k.endsWith("sn") || k === "sn") {
                const val = String(normalized[k] || "").trim();
                if (val && val !== "undefined" && val !== "null") return val;
              }
            }
            return "";
          };

          // Accurate SKU extractor (ignores composite identifier headers)
          const getSkuVal = () => {
            const keys = Object.keys(normalized);
            for (const k of keys) {
              if (["skunumber", "skuno", "sku", "partnumber", "partno", "pn"].includes(k)) {
                const val = String(normalized[k] || "").trim();
                if (val && val !== "undefined" && val !== "null") return val;
              }
            }
            for (const k of keys) {
              if (k.includes("identifier") || k.includes("serial") || k.includes("name") || k.includes("desc")) continue;
              if (k.includes("sku") || k.includes("partnumber") || k.includes("partno") || k === "pn") {
                const val = String(normalized[k] || "").trim();
                if (val && val !== "undefined" && val !== "null") return val;
              }
            }
            return "";
          };

          // Accurate Item Name extractor
          const getNameVal = () => {
            const keys = Object.keys(normalized);
            for (const k of keys) {
              if (["itemname", "itemdescription", "description", "productname", "product", "item"].includes(k)) {
                const val = String(normalized[k] || "").trim();
                if (val && val !== "undefined" && val !== "null") return val;
              }
            }
            for (const k of keys) {
              if (k.includes("identifier") || k.includes("serial") || k.includes("sku") || k.includes("part")) continue;
              if (k.includes("name") || k.includes("desc") || k.includes("item") || k.includes("product")) {
                const val = String(normalized[k] || "").trim();
                if (val && val !== "undefined" && val !== "null") return val;
              }
            }
            return "";
          };

          // Accurate Category extractor
          const getCatVal = () => {
            const keys = Object.keys(normalized);
            for (const k of keys) {
              if (["categoryname", "category", "cat", "type"].includes(k)) {
                const val = String(normalized[k] || "").trim();
                if (val && val !== "undefined" && val !== "null") return val;
              }
            }
            for (const k of keys) {
              if (k.includes("subcat") || k.includes("item") || k.includes("serial") || k.includes("sku") || k.includes("part")) continue;
              if (k.includes("cat") || k.includes("type")) {
                const val = String(normalized[k] || "").trim();
                if (val && val !== "undefined" && val !== "null") return val;
              }
            }
            return "";
          };

          // Identifier extractor (only if distinct from serial/sku/name)
          const getIdVal = () => {
            const keys = Object.keys(normalized);
            for (const k of keys) {
              if (["identifier", "ident", "code", "barcode"].includes(k)) {
                const val = String(normalized[k] || "").trim();
                if (val && val !== "undefined" && val !== "null") return val;
              }
            }
            for (const k of keys) {
              if (k.startsWith("identifier") || k.includes("code")) {
                const val = String(normalized[k] || "").trim();
                if (val && val !== "undefined" && val !== "null") return val;
              }
            }
            return "";
          };

          const serialVal = getSerialVal();
          const skuVal = getSkuVal();
          const nameVal = getNameVal();
          const catVal = getCatVal();
          const idVal = getIdVal();

          const getValByKeywords = (keywords) => {
            for (const k of Object.keys(normalized)) {
              for (const kw of keywords) {
                if (k === kw || k.includes(kw)) {
                  const val = String(normalized[k] || "").trim();
                  if (val !== "" && val !== "undefined" && val !== "null") return val;
                }
              }
            }
            return "";
          };

          const rawPrice = getValByKeywords(["saleprice", "sellingprice", "unitprice", "rate", "price", "unitcost", "cost"]);
          const rawQty = getValByKeywords(["countquantity", "quantity", "count", "qty", "units", "pieces", "pcs"]) || "1";
          const rawTotal = getValByKeywords(["totalprice", "totalcost", "totalamount", "linetotal", "amount", "total"]);

          // If row has no identifying info at all, ignore it completely (empty row)
          if (!serialVal && !skuVal && !idVal && !nameVal && !catVal) {
            console.log(`[SALE SHEET UPLOAD] Row #${idx + 1} has no identifier, name, or category. Ignored.`);
            return;
          }

          const reqQty = Math.max(1, parseInt(rawQty) || 1);
          let priceNum = parseFloat(rawPrice) || 0;
          if (priceNum <= 0 && rawTotal && !isNaN(parseFloat(rawTotal))) {
            priceNum = parseFloat(rawTotal) / reqQty;
          }

          // === SMART INVENTORY ITEM MATCHING ===
          let matchedItem = null;

          // 1. Match by Serial Number (Exact or Clean)
          // CRITICAL: If row specifies a Serial Number, ONLY match the item whose Serial Number matches the sheet!
          // NEVER fall back to item name or category when a serial number is provided,
          // because that causes all rows to match the same first stock item!
          if (serialVal) {
            const cSerial = cleanStr(serialVal);
            matchedItem = availableItems.find(
              (it) => !usedItemIds.has(it.id) && it.serial_number && cleanStr(it.serial_number) === cSerial
            );
          } else {
            // Serial number not specified in this row (e.g. bulk accessories, RAM, NVMe, Mouse, Licenses)
            // 2. Match by SKU (Exact or Clean)
            if (skuVal) {
              const cSku = cleanStr(skuVal);
              matchedItem = availableItems.find(
                (it) => !usedItemIds.has(it.id) && it.sku && cleanStr(it.sku) === cSku
              );
            }

            // 3. Match by Identifier (SKU, Item Name, or Category)
            if (!matchedItem && idVal) {
              const cId = cleanStr(idVal);
              // 3a. SKU match
              matchedItem = availableItems.find(
                (it) => !usedItemIds.has(it.id) && it.sku && cleanStr(it.sku) === cId
              );
              // 3b. Item Name exact or clean match
              if (!matchedItem) {
                matchedItem = availableItems.find(
                  (it) => !usedItemIds.has(it.id) && it.item_name && cleanStr(it.item_name) === cId
                );
              }
              // 3c. Category exact match
              if (!matchedItem) {
                matchedItem = availableItems.find(
                  (it) => !usedItemIds.has(it.id) && it.category_name && cleanStr(it.category_name) === cId
                );
              }
              // 3d. Substring match
              if (!matchedItem && cId.length >= 3) {
                matchedItem = availableItems.find(
                  (it) =>
                    !usedItemIds.has(it.id) &&
                    ((it.item_name && cleanStr(it.item_name).includes(cId)) ||
                     (it.category_name && cleanStr(it.category_name).includes(cId)))
                );
              }
            }

            // 4. Match by Item Name
            if (!matchedItem && nameVal) {
              const cName = cleanStr(nameVal);
              // 4a. Exact clean name match
              matchedItem = availableItems.find(
                (it) => !usedItemIds.has(it.id) && it.item_name && cleanStr(it.item_name) === cName
              );
              // 4b. Substring match (ensure length >= 4 to avoid 2-letter collisions)
              if (!matchedItem) {
                matchedItem = availableItems.find(
                  (it) => !usedItemIds.has(it.id) && it.item_name && cleanStr(it.item_name).includes(cName)
                );
              }
              // 4c. Token overlap match (e.g. "Kingston NV2 512GB NVMe SSD" vs "Kingston NV2 512GB NVMe M.2 SSD")
              if (!matchedItem) {
                const tokens = nameVal.toLowerCase().split(/\s+/).filter((t) => t.length >= 3);
                let bestMatch = null;
                let highestCount = 0;
                for (const it of availableItems) {
                  if (usedItemIds.has(it.id)) continue;
                  const itLower = (it.item_name || "").toLowerCase();
                  const count = tokens.filter((t) => itLower.includes(t)).length;
                  if (count > highestCount && count >= Math.ceil(tokens.length * 0.5)) {
                    highestCount = count;
                    bestMatch = it;
                  }
                }
                if (bestMatch) {
                  matchedItem = bestMatch;
                }
              }
            }

            // 5. Match by Category Name (essential for bulk categories: NVME, RAM, Mouse, Mouse Pad, Bags, Licenses)
            if (!matchedItem && catVal) {
              const cCat = cleanStr(catVal);
              matchedItem = availableItems.find(
                (it) => !usedItemIds.has(it.id) && it.category_name && cleanStr(it.category_name) === cCat
              );
              if (!matchedItem) {
                matchedItem = availableItems.find(
                  (it) =>
                    !usedItemIds.has(it.id) &&
                    it.category_name &&
                    (cleanStr(it.category_name).includes(cCat) || cCat.includes(cleanStr(it.category_name)))
                );
              }
            }
          }

          // Result of match for this row
          if (matchedItem) {
            // Reserve this unique stock item so later rows do not duplicate it
            if (matchedItem.serial_number || parseInt(matchedItem.quantity) <= 1) {
              usedItemIds.add(matchedItem.id);
            }

            const stockQty = parseInt(matchedItem.quantity) || 1;
            const finalQty = Math.min(reqQty, stockQty);
            if (reqQty > stockQty) {
              stockExceededItems.push(`${matchedItem.item_name} (Requested: ${reqQty}, In-Stock: ${stockQty})`);
            }

            const fallbackPrice =
              priceNum > 0
                ? String(priceNum)
                : matchedItem.sale_price && parseFloat(matchedItem.sale_price) > 0
                ? String(matchedItem.sale_price)
                : matchedItem.price && parseFloat(matchedItem.price) > 0
                ? String(matchedItem.price)
                : "";

            newRows.push({
              rowId: `sale-bulk-${Date.now()}-${idx}-${Math.floor(Math.random() * 1000)}`,
              item_id: matchedItem.id,
              item_name: matchedItem.item_name || "",
              sku: matchedItem.sku || "",
              serial_number: matchedItem.serial_number || "",
              part_number: matchedItem.part_number || "",
              category_name: matchedItem.category_name || "",
              subcategory_name: matchedItem.subcategory_name || "",
              current_stock: stockQty,
              quantity: finalQty,
              unit_price: fallbackPrice,
            });
            console.log(`[SALE SHEET UPLOAD] Row #${idx + 1} Matched -> ID: ${matchedItem.id} (${matchedItem.item_name}), Qty: ${finalQty}`);
          } else {
            const label = serialVal ? `SN: ${serialVal}` : (nameVal || catVal || skuVal || idVal || `Row #${idx + 1}`);
            // Check if already sold in general inventory
            const anyItem = items.find((it) => {
              const sMatch = serialVal && it.serial_number && cleanStr(it.serial_number) === cleanStr(serialVal);
              const skuMatch = skuVal && it.sku && cleanStr(it.sku) === cleanStr(skuVal);
              return sMatch || skuMatch;
            });

            if (anyItem && (anyItem.is_sale === 1 || anyItem.is_sale === true || anyItem.is_sale === "1")) {
              alreadySoldItems.push(label);
            } else {
              notFoundItems.push(label);
            }
            console.warn(`[SALE SHEET UPLOAD] Row #${idx + 1} NOT MATCHED in stock: "${label}"`, row);
          }
        });

        console.log("%c[SALE SHEET UPLOAD] Final Import Summary:", "color: #059669; font-weight: bold; font-size: 13px;", {
          totalContentRows: contentRows.length,
          successfullyImported: newRows.length,
          notFoundCount: notFoundItems.length,
          alreadySoldCount: alreadySoldItems.length,
          importedRows: newRows,
        });

        if (newRows.length > 0) {
          setSaleRows((prev) => {
            const existingFilled = prev.filter((r) => r.item_id);
            return [...existingFilled, ...newRows];
          });
          toast.success(`Successfully loaded ${newRows.length} item(s) from sheet into sale!`);
        } else {
          toast.warning("No matching in-stock inventory items were found in the uploaded sheet. Check console (F12) for details.");
        }

        if (alreadySoldItems.length > 0) {
          toast.error(`${alreadySoldItems.length} item(s) already sold: ${alreadySoldItems.slice(0, 3).join(", ")}${alreadySoldItems.length > 3 ? "..." : ""}`);
        }

        if (notFoundItems.length > 0) {
          toast.warning(`${notFoundItems.length} item(s) not found in stock: ${notFoundItems.slice(0, 3).join(", ")}${notFoundItems.length > 3 ? "..." : ""}`);
        }

        if (stockExceededItems.length > 0) {
          toast.info(`Some quantities capped to available stock: ${stockExceededItems.slice(0, 2).join("; ")}`);
        }
      } catch (err) {
        console.error("%c[SALE SHEET UPLOAD ERROR]:", "color: #DC2626; font-weight: bold; font-size: 14px;", err);
        console.error("Stack trace:", err.stack);
        toast.error(`Failed to parse sheet: ${err.message || "Check browser console"}`);
      } finally {
        if (fileInputRef.current) fileInputRef.current.value = "";
      }
    };
    reader.readAsArrayBuffer(file);
  };

  // Handle price update on row
  const handleRowPriceChange = (rowIndex, value) => {
    setSaleRows((prev) => {
      const updated = [...prev];
      updated[rowIndex] = { ...updated[rowIndex], unit_price: value };
      return updated;
    });
  };

  // Add new row
  const handleAddRow = () => {
    setSaleRows((prev) => [...prev, createInitialRow()]);
  };

  // Delete row
  const handleDeleteRow = (rowIndex) => {
    setSaleRows((prev) => prev.filter((_, idx) => idx !== rowIndex));
  };

  // Reset entire sale form (empties all items)
  const handleResetForm = () => {
    setEditingSale(null);
    setSaleRows([]);
    setSelectedClientId("");
    setInvoiceNumber("");
    setSaleDate(new Date().toISOString().split("T")[0]);
  };

  // Calculations for items and grand total
  const validItemsCount = saleRows.filter((r) => r.item_id).length;
  const grandTotalAmount = useMemo(() => {
    return saleRows.reduce((acc, row) => {
      if (!row.item_id) return acc;
      const price = parseFloat(row.unit_price) || 0;
      const qty = parseInt(row.quantity) || 1;
      return acc + price * qty;
    }, 0);
  }, [saleRows]);

  // Complete Sale and Save
  const handleCompleteSale = async (e) => {
    e.preventDefault();

    if (!selectedClientId) {
      toast.warning("Please select a Client!");
      return;
    }

    if (saleRows.length === 0 || !saleRows.some((r) => r.item_id)) {
      toast.warning("Please select at least one item to sell!");
      return;
    }

    // Validate that all rows have a selected item
    for (let i = 0; i < saleRows.length; i++) {
      const row = saleRows[i];
      if (!row.item_id) {
        toast.warning(`Row #${i + 1}: Please select an item or remove the empty row.`);
        return;
      }
    }

    try {
      setSubmitting(true);

      const payload = {
        client_id: parseInt(selectedClientId),
        sale_date: saleDate,
        invoice_number: invoiceNumber ? invoiceNumber.trim() : (editingSale ? editingSale.originalInvoiceNumber : undefined),
        items: saleRows.map((r) => {
          const sPrice = parseFloat(r.unit_price) || 0;
          const qty = parseInt(r.quantity) || 1;
          return {
            id: parseInt(r.item_id),
            item_id: parseInt(r.item_id),
            serial_number: r.serial_number ? String(r.serial_number).trim() : undefined,
            sku: r.sku,
            part_number: r.part_number,
            quantity: qty,
            price: sPrice,
            sale_price: sPrice,
            unit_price: sPrice,
            total_price: sPrice * qty,
          };
        }),
      };

      if (editingSale) {
        const res = await saleApi.update(editingSale.id || editingSale.originalInvoiceNumber, payload);
        toast.success(res?.message || "Sale invoice updated successfully!");
        setEditingSale(null);
        handleResetForm();
        setSearchParams({ tab: "record" });
        await fetchData(true);
        return;
      }

      const res = await saleApi.create(payload);
      toast.success(res.message || "Sale completed successfully!");

      handleResetForm();
      setSearchParams({ tab: "record" });
      await fetchData(true);
    } catch (err) {
      toast.error(err.message || "Failed to record sale");
    } finally {
      setSubmitting(false);
    }
  };

  // Delete sale confirmation
  const handleDeleteSale = async () => {
    if (!deleteModal.sale) return;
    try {
      const idToDelete = deleteModal.sale.id || deleteModal.sale.invoice_number;
      await saleApi.delete(idToDelete);
      toast.success("Sale record deleted successfully");
      setDeleteModal({ open: false, sale: null });
      fetchData();
      fetchSaleHistory();
    } catch (err) {
      toast.error(err.response?.data?.error || err.message || "Failed to delete sale record");
    }
  };

  // Selected client object
  const activeClient = clients.find((c) => String(c.id) === String(selectedClientId));

  // =========================================================================
  // SALE HISTORY: Group Sales by Invoice Number
  // =========================================================================
  const groupedInvoices = useMemo(() => {
    const map = {};

    sales.forEach((s) => {
      const invKey = s.invoice_number ? String(s.invoice_number).trim() : `INV-LEGACY-${s.id}`;

      if (!map[invKey]) {
        map[invKey] = {
          id: s.id,
          invoice_number: invKey,
          display_invoice_number: s.invoice_number ? String(s.invoice_number).trim() : `INV-LEGACY-${s.id}`,
          has_real_invoice: Boolean(s.invoice_number),
          client_id: s.client_id,
          client_name: s.client_name || "Unknown Client",
          client_contact: s.client_contact || "",
          client_contact_person: s.client_contact_person || "",
          sale_date: s.sale_date || s.created_at,
          created_by_name: s.created_by_name || "",
          total_items: 0,
          total_price: 0,
          items: [],
        };
      }

      const returnedAmt = parseFloat(s.total_returned_amount) || parseFloat(s.returned_amount) || 0;
      const returnedItemsCount = parseInt(s.total_returned_items_count) || 0;

      if (Array.isArray(s.items) && s.items.length > 0) {
        map[invKey].items.push(...s.items);
        const unitsCount = s.items.reduce((sum, it) => sum + (parseInt(it.quantity) || 1), 0);
        map[invKey].total_items += unitsCount;

        // Sum current active sold items
        const activeItemsSum = s.items.reduce(
          (acc, it) => acc + (parseFloat(it.sale_price || it.price || it.unit_price) || 0) * (parseInt(it.quantity) || 1),
          0
        );
        map[invKey].total_price += activeItemsSum;
      } else {
        if (returnedAmt > 0) {
          // All items in invoice were returned
          map[invKey].total_items += 0;
          map[invKey].total_price += 0;
        } else {
          const qty = parseInt(s.quantity) || parseInt(s.total_items_count) || 0;
          const price = parseFloat(s.total_price) || parseFloat(s.total_amount) || 0;
          map[invKey].total_items += qty;
          map[invKey].total_price += price;
          map[invKey].items.push(s);
        }
      }

      if (returnedAmt > 0) {
        map[invKey].returned_amount = (map[invKey].returned_amount || 0) + returnedAmt;
        map[invKey].returned_items = (map[invKey].returned_items || 0) + returnedItemsCount;
      }
    });

    return Object.values(map).sort((a, b) => new Date(b.sale_date || 0) - new Date(a.sale_date || 0));
  }, [sales]);

  const totalSaleAmount = useMemo(() => {
    return groupedInvoices.reduce((sum, inv) => sum + (parseFloat(inv.total_price) || 0), 0);
  }, [groupedInvoices]);

  const totalSaleUnits = useMemo(() => {
    return groupedInvoices.reduce((sum, inv) => sum + (parseInt(inv.total_items, 10) || 0), 0);
  }, [groupedInvoices]);

  // Debounce search query to prevent frequent API calls
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedHistorySearch(historySearch);
      setHistoryPage(1);
    }, 350);
    return () => clearTimeout(timer);
  }, [historySearch]);

  const fetchSaleHistory = async (
    page = historyPage,
    limit = historyPageSize,
    search = debouncedHistorySearch
  ) => {
    try {
      setHistoryLoading(true);
      const params = {
        page,
        limit,
      };
      if (search && search.trim()) {
        params.search = search.trim();
      }
      const res = await saleApi.getAll(params);
      const resPagination = res?.pagination || res?.data?.pagination;
      const resData = Array.isArray(res?.data) ? res.data : Array.isArray(res) ? res : [];

      setHistoryInvoices(resData);
      if (resPagination) {
        setHistoryPagination(resPagination);
      } else {
        setHistoryPagination({
          total: resData.length,
          page,
          limit,
          totalPages: Math.ceil(resData.length / limit) || 1,
        });
      }
    } catch (err) {
      console.error("Fetch sale history error:", err);
      toast.error("Failed to load sale history from server");
    } finally {
      setHistoryLoading(false);
    }
  };

  useEffect(() => {
    if (activeTab === "history") {
      fetchSaleHistory(historyPage, historyPageSize, debouncedHistorySearch);
    }
  }, [activeTab, debouncedHistorySearch, historyPage, historyPageSize]);

  // Pagination calculations for Sale History based on backend data
  const totalInvoices = historyPagination.total || 0;
  const totalHistoryPages = historyPagination.totalPages || 1;
  const historyStartIndex = totalInvoices === 0 ? 0 : (historyPagination.page - 1) * historyPagination.limit + 1;
  const historyEndIndex = Math.min(
    (historyPagination.page - 1) * historyPagination.limit + historyInvoices.length,
    totalInvoices
  );

  const handleOpenInvoicePage = (invoice) => {
    const invNum = invoice.invoice_number || invoice.display_invoice_number;
    if (invNum) {
      navigate(`/sales/invoice/${encodeURIComponent(invNum)}`, {
        state: { invoice },
      });
    }
  };

  return (
    <div className="fade-in">
      {/* Top Header & Page Navigation Tabs */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexWrap: "wrap",
          gap: 16,
          marginBottom: 20,
        }}
      >
        <div>
          <h2 style={{ fontSize: "1.5rem", fontWeight: 800, color: "var(--navy)" }}>
            {activeTab === "record" ? "Record Client Sale" : "Sale History"}
          </h2>
        </div>

        {/* View Mode Toggle: Record Sale vs Sale History */}
        <div
          style={{
            display: "flex",
            backgroundColor: "#fff",
            padding: 4,
            borderRadius: "var(--radius-md)",
            border: "1px solid var(--slate-200)",
            boxShadow: "var(--shadow-sm)",
            gap: 4,
          }}
        >
          <button
            type="button"
            onClick={() => handleTabChange("record")}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              padding: "9px 18px",
              borderRadius: 8,
              border: activeTab === "record" ? "1.5px solid var(--primary)" : "1px solid var(--slate-200)",
              backgroundColor: activeTab === "record" ? "var(--primary-light)" : "#fff",
              color: activeTab === "record" ? "var(--primary)" : "var(--slate-600)",
              fontWeight: 700,
              fontSize: "0.88rem",
              cursor: "pointer",
              transition: "all 0.15s ease",
              boxShadow: activeTab === "record" ? "0 2px 6px rgba(6, 78, 59, 0.1)" : "none",
            }}
          >
            <FiPlus size={16} />
            Record Client Sale
          </button>

          <button
            type="button"
            onClick={() => handleTabChange("history")}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              padding: "9px 18px",
              borderRadius: 8,
              border: activeTab === "history" ? "1.5px solid var(--primary)" : "1px solid var(--slate-200)",
              backgroundColor: activeTab === "history" ? "var(--primary-light)" : "#fff",
              color: activeTab === "history" ? "var(--primary)" : "var(--slate-600)",
              fontWeight: 700,
              fontSize: "0.88rem",
              cursor: "pointer",
              transition: "all 0.15s ease",
              boxShadow: activeTab === "history" ? "0 2px 6px rgba(6, 78, 59, 0.1)" : "none",
            }}
          >
            <FiClock size={16} />
            Sale History
          </button>

          <button
            type="button"
            onClick={() => handleTabChange("returns")}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              padding: "9px 18px",
              borderRadius: 8,
              border: activeTab === "returns" ? "1.5px solid var(--primary)" : "1px solid var(--slate-200)",
              backgroundColor: activeTab === "returns" ? "var(--primary-light)" : "#fff",
              color: activeTab === "returns" ? "var(--primary)" : "var(--slate-600)",
              fontWeight: 700,
              fontSize: "0.88rem",
              cursor: "pointer",
              transition: "all 0.15s ease",
              boxShadow: activeTab === "returns" ? "0 2px 6px rgba(6, 78, 59, 0.1)" : "none",
            }}
          >
            <FiRotateCcw size={16} />
            Sale Returns
          </button>
        </div>
      </div>

      {/* Top Dashboard-Style Summary Cards */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
          gap: 20,
          marginBottom: 24,
        }}
      >
        {/* Card 1: Total Sale Amount */}
        <div
          style={{
            backgroundColor: "#fff",
            borderRadius: "var(--radius-lg)",
            padding: "22px 24px",
            border: "1px solid var(--slate-200)",
            boxShadow: "var(--shadow-sm)",
            display: "block",
            transition: "transform 0.2s, box-shadow 0.2s",
            cursor: "pointer",
          }}
          onClick={() => handleTabChange("history")}
          title="Click to view sale history"
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = "translateY(-2px)";
            e.currentTarget.style.boxShadow = "var(--shadow-md)";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = "none";
            e.currentTarget.style.boxShadow = "var(--shadow-sm)";
          }}
        >
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 12 }}>
            <span style={{ fontSize: "0.82rem", fontWeight: 700, color: "var(--slate-500)", textTransform: "uppercase", letterSpacing: "0.05em" }}>
              Total Sale Amount
            </span>
            <div style={{ padding: 10, borderRadius: 10, backgroundColor: "#F3E8FF", color: "#7E22CE" }}>
              <FiShoppingBag size={22} />
            </div>
          </div>
          <div
            style={{
              fontSize: "1.65rem",
              fontWeight: 800,
              color: "#7E22CE",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
            title={`Rs. ${totalSaleAmount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
          >
            Rs. {totalSaleAmount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
          </div>
          <div style={{ fontSize: "0.78rem", color: "var(--slate-500)", marginTop: 6, display: "flex", alignItems: "center", gap: 5 }}>
            <span style={{ width: 8, height: 8, borderRadius: "50%", backgroundColor: "#7E22CE" }} />
            <span>{totalSaleUnits.toLocaleString()} items sold across {groupedInvoices.length} invoices</span>
          </div>
        </div>

        {/* Card 2: Total Sold Items */}
        <div
          style={{
            backgroundColor: "#fff",
            borderRadius: "var(--radius-lg)",
            padding: "22px 24px",
            border: "1px solid var(--slate-200)",
            boxShadow: "var(--shadow-sm)",
            display: "block",
            transition: "transform 0.2s, box-shadow 0.2s",
            cursor: "pointer",
          }}
          onClick={() => handleTabChange("history")}
          title="Click to view sale history"
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = "translateY(-2px)";
            e.currentTarget.style.boxShadow = "var(--shadow-md)";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = "none";
            e.currentTarget.style.boxShadow = "var(--shadow-sm)";
          }}
        >
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 12 }}>
            <span style={{ fontSize: "0.82rem", fontWeight: 700, color: "var(--slate-500)", textTransform: "uppercase", letterSpacing: "0.05em" }}>
              Total Sold Items
            </span>
            <div style={{ padding: 10, borderRadius: 10, backgroundColor: "#E0F2FE", color: "#0284C7" }}>
              <FiBox size={22} />
            </div>
          </div>
          <div
            style={{
              fontSize: "1.65rem",
              fontWeight: 800,
              color: "var(--navy)",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {totalSaleUnits.toLocaleString()} <span style={{ fontSize: "1rem", fontWeight: 600, color: "var(--slate-500)" }}>Units</span>
          </div>
          <div style={{ fontSize: "0.78rem", color: "var(--slate-500)", marginTop: 6, display: "flex", alignItems: "center", gap: 5 }}>
            <span style={{ width: 8, height: 8, borderRadius: "50%", backgroundColor: "#0284C7" }} />
            <span>Total asset inventory units sold</span>
          </div>
        </div>

        {/* Card 3: Total Sales Invoices */}
        <div
          style={{
            backgroundColor: "#fff",
            borderRadius: "var(--radius-lg)",
            padding: "22px 24px",
            border: "1px solid var(--slate-200)",
            boxShadow: "var(--shadow-sm)",
            display: "block",
            transition: "transform 0.2s, box-shadow 0.2s",
            cursor: "pointer",
          }}
          onClick={() => handleTabChange("history")}
          title="Click to view sale history"
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = "translateY(-2px)";
            e.currentTarget.style.boxShadow = "var(--shadow-md)";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = "none";
            e.currentTarget.style.boxShadow = "var(--shadow-sm)";
          }}
        >
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 12 }}>
            <span style={{ fontSize: "0.82rem", fontWeight: 700, color: "var(--slate-500)", textTransform: "uppercase", letterSpacing: "0.05em" }}>
              Total Sales Invoices
            </span>
            <div style={{ padding: 10, borderRadius: 10, backgroundColor: "#CCFBF1", color: "#0D9488" }}>
              <FiFileText size={22} />
            </div>
          </div>
          <div
            style={{
              fontSize: "1.65rem",
              fontWeight: 800,
              color: "#0D9488",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {groupedInvoices.length} <span style={{ fontSize: "1rem", fontWeight: 600, color: "var(--slate-500)" }}>Invoices</span>
          </div>
          <div style={{ fontSize: "0.78rem", color: "var(--slate-500)", marginTop: 6, display: "flex", alignItems: "center", gap: 5 }}>
            <span style={{ width: 8, height: 8, borderRadius: "50%", backgroundColor: "#0D9488" }} />
            <span>Total client sales invoices generated</span>
          </div>
        </div>
      </div>

      {/* ========================================================================= */}
      {/* VIEW 1: RECORD CLIENT SALE                                                */}
      {/* ========================================================================= */}
      {activeTab === "record" && (
        <form onSubmit={handleCompleteSale} className="fade-in">
          {editingSale && (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                backgroundColor: "#EFF6FF",
                border: "1.5px solid #3B82F6",
                borderRadius: 12,
                padding: "16px 20px",
                marginBottom: 20,
                gap: 14,
                flexWrap: "wrap",
                boxShadow: "0 2px 8px rgba(37, 99, 235, 0.08)",
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                <div
                  style={{
                    width: 42,
                    height: 42,
                    borderRadius: 10,
                    backgroundColor: "#DBEAFE",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: "#1D4ED8",
                    flexShrink: 0,
                  }}
                >
                  <FiEdit size={22} />
                </div>
                <div>
                  <div style={{ fontWeight: 800, fontSize: "1rem", color: "#1E3A8A" }}>
                    Editing Sale Invoice: #{editingSale.originalInvoiceNumber}
                  </div>
                  <div style={{ fontSize: "0.82rem", color: "#2563EB", marginTop: 2 }}>
                    You can add new items to sell, remove items (restoring them to stock), modify sale prices, or change customer & invoice details.
                  </div>
                </div>
              </div>
              <button
                type="button"
                onClick={handleCancelEdit}
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 6,
                  padding: "8px 18px",
                  borderRadius: 8,
                  border: "1.5px solid #93C5FD",
                  backgroundColor: "#fff",
                  color: "#1D4ED8",
                  fontWeight: 700,
                  fontSize: "0.85rem",
                  cursor: "pointer",
                  transition: "all 0.15s ease",
                }}
              >
                <FiX size={16} /> Cancel Edit
              </button>
            </div>
          )}

          {/* Step 1: Client & Sale Information Card */}
          <div
            style={{
              backgroundColor: "#fff",
              borderRadius: "var(--radius-lg)",
              border: "1px solid var(--slate-200)",
              boxShadow: "var(--shadow-sm)",
              padding: "22px 24px",
              marginBottom: 20,
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 16 }}>
              <div
                style={{
                  width: 36,
                  height: 36,
                  borderRadius: 8,
                  backgroundColor: "var(--primary-light)",
                  color: "var(--primary)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <FiUsers size={20} />
              </div>
              <div>
                <h3 style={{ fontSize: "1.12rem", fontWeight: 800, color: "var(--navy)", margin: 0 }}>
                  1. Client & Invoice Information
                </h3>
              </div>
            </div>

            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
                gap: 18,
              }}
            >
              {/* Select Client (Searchable, NOT pre-selected) */}
              <div>
                <label style={{ display: "block", fontSize: "0.84rem", fontWeight: 700, color: "var(--slate-700)", marginBottom: 6 }}>
                  Select Client / Customer *
                </label>
                <SearchableClientSelect
                  clients={clients}
                  selectedClientId={selectedClientId}
                  onSelect={(id) => setSelectedClientId(id)}
                  placeholder="-- Choose or search client customer --"
                />
              </div>

              {/* Sale Date */}
              <div>
                <label style={{ display: "block", fontSize: "0.84rem", fontWeight: 700, color: "var(--slate-700)", marginBottom: 6 }}>
                  Sale Date *
                </label>
                <input
                  type="date"
                  required
                  value={saleDate}
                  onChange={(e) => setSaleDate(e.target.value)}
                  style={{
                    width: "100%",
                    height: 42,
                    padding: "9px 14px",
                    borderRadius: 8,
                    border: "1.5px solid var(--slate-300)",
                    backgroundColor: "#fff",
                    fontWeight: 600,
                    color: "var(--navy)",
                    fontSize: "0.86rem",
                    boxSizing: "border-box",
                  }}
                />
              </div>

              {/* Invoice Number (Shared for all items) */}
              <div>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 6 }}>
                  <label style={{ fontSize: "0.84rem", fontWeight: 700, color: "var(--slate-700)" }}>
                    Invoice #
                  </label>
                  <span style={{ fontSize: "0.72rem", color: "var(--slate-400)", fontWeight: 500 }}>
                    (Shared for all items)
                  </span>
                </div>
                <div style={{ position: "relative" }}>
                  <FiFileText
                    size={16}
                    style={{
                      position: "absolute",
                      left: 12,
                      top: "50%",
                      transform: "translateY(-50%)",
                      color: "var(--slate-400)",
                      pointerEvents: "none",
                    }}
                  />
                  <input
                    type="text"
                    placeholder="e.g. INV-2026-001 (auto-generated if empty)"
                    value={invoiceNumber}
                    onChange={(e) => setInvoiceNumber(e.target.value)}
                    style={{
                      width: "100%",
                      height: 42,
                      padding: "9px 14px 9px 36px",
                      borderRadius: 8,
                      border: "1.5px solid var(--slate-300)",
                      backgroundColor: "#fff",
                      fontWeight: 600,
                      color: "var(--navy)",
                      fontSize: "0.86rem",
                      boxSizing: "border-box",
                    }}
                  />
                </div>
              </div>
            </div>

            {/* Client Details Preview Card if selected */}
            {activeClient && (
              <div
                style={{
                  marginTop: 16,
                  padding: "12px 16px",
                  borderRadius: 8,
                  backgroundColor: "var(--slate-50)",
                  border: "1px solid var(--slate-200)",
                  display: "flex",
                  alignItems: "center",
                  gap: 20,
                  flexWrap: "wrap",
                  fontSize: "0.82rem",
                }}
              >
                <div>
                  <span style={{ color: "var(--slate-400)", textTransform: "uppercase", fontSize: "0.7rem", display: "block", fontWeight: 700 }}>
                    Client Name
                  </span>
                  <strong style={{ color: "var(--navy)" }}>{activeClient.company_name}</strong>
                </div>

                {activeClient.contact_person_name && (
                  <div>
                    <span style={{ color: "var(--slate-400)", textTransform: "uppercase", fontSize: "0.7rem", display: "block", fontWeight: 700 }}>
                      Representative
                    </span>
                    <strong style={{ color: "var(--slate-700)" }}>{activeClient.contact_person_name}</strong>
                  </div>
                )}

                {activeClient.contact && (
                  <div>
                    <span style={{ color: "var(--slate-400)", textTransform: "uppercase", fontSize: "0.7rem", display: "block", fontWeight: 700 }}>
                      Contact Phone
                    </span>
                    <strong style={{ fontFamily: "monospace", color: "var(--slate-700)" }}>{activeClient.contact}</strong>
                  </div>
                )}

                {activeClient.ntn && (
                  <div>
                    <span style={{ color: "var(--slate-400)", textTransform: "uppercase", fontSize: "0.7rem", display: "block", fontWeight: 700 }}>
                      NTN #
                    </span>
                    <strong style={{ fontFamily: "monospace", color: "var(--primary)" }}>{activeClient.ntn}</strong>
                  </div>
                )}

                {activeClient.city && (
                  <div>
                    <span style={{ color: "var(--slate-400)", textTransform: "uppercase", fontSize: "0.7rem", display: "block", fontWeight: 700 }}>
                      City / Address
                    </span>
                    <span style={{ color: "var(--slate-600)" }}>{activeClient.city}</span>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Step 2: Configure Items to Sell Card */}
          <div
            style={{
              backgroundColor: "#fff",
              borderRadius: "var(--radius-lg)",
              border: "1px solid var(--slate-200)",
              boxShadow: "var(--shadow-sm)",
              padding: "20px 24px",
              marginBottom: 20,
            }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                flexWrap: "wrap",
                gap: 12,
                marginBottom: 16,
                paddingBottom: 14,
                borderBottom: "1px solid var(--slate-100)",
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <div
                  style={{
                    width: 36,
                    height: 36,
                    borderRadius: 8,
                    backgroundColor: "var(--primary-light)",
                    color: "var(--primary)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <FiBox size={18} />
                </div>
                <div>
                  <h3 style={{ fontSize: "1.12rem", fontWeight: 800, color: "var(--navy)", margin: 0 }}>
                    2. Configure Items to Sell ({saleRows.length} {saleRows.length === 1 ? "Row" : "Rows"})
                  </h3>
                </div>
              </div>

              <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                {/* Download Sample File Button */}
                <button
                  type="button"
                  onClick={handleDownloadSampleFile}
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 6,
                    padding: "8px 14px",
                    borderRadius: 8,
                    border: "1px solid var(--slate-300)",
                    backgroundColor: "#fff",
                    color: "var(--slate-700)",
                    fontWeight: 700,
                    fontSize: "0.82rem",
                    cursor: "pointer",
                    transition: "all 0.15s ease",
                  }}
                  title="Download sample Excel template for bulk upload"
                >
                  <FiDownload size={14} /> Download Sample File
                </button>

                {/* Bulk Upload Button */}
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 6,
                    padding: "8px 14px",
                    borderRadius: 8,
                    border: "1px solid var(--primary)",
                    backgroundColor: "var(--primary-light)",
                    color: "var(--primary)",
                    fontWeight: 700,
                    fontSize: "0.82rem",
                    cursor: "pointer",
                    transition: "all 0.15s ease",
                  }}
                  title="Upload Excel file with serial numbers and sale prices"
                >
                  <FiUpload size={14} /> Bulk Upload (Excel)
                </button>

                {/* Hidden file input */}
                <input
                  type="file"
                  ref={fileInputRef}
                  accept=".xlsx, .xls, .csv"
                  onChange={handleFileUpload}
                  style={{ display: "none" }}
                />

                {/* Add Row Button */}
                <button
                  type="button"
                  onClick={handleAddRow}
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 6,
                    padding: "8px 16px",
                    borderRadius: 8,
                    border: "none",
                    backgroundColor: "var(--primary)",
                    color: "#fff",
                    fontWeight: 700,
                    fontSize: "0.84rem",
                    cursor: "pointer",
                    boxShadow: "0 2px 4px rgba(6,78,59,0.2)",
                    transition: "all 0.15s ease",
                  }}
                >
                  <FiPlus size={15} /> Add Another Item
                </button>
              </div>
            </div>

            {/* Configured Item Cards */}
            <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
              {saleRows.length === 0 ? (
                <div
                  style={{
                    padding: "36px 20px",
                    textAlign: "center",
                    backgroundColor: "#F8FAFC",
                    border: "1.5px dashed var(--slate-300)",
                    borderRadius: 10,
                  }}
                >
                  <FiBox size={30} style={{ color: "var(--slate-400)", marginBottom: 10 }} />
                  <p style={{ margin: "0 0 8px 0", color: "var(--slate-700)", fontWeight: 700, fontSize: "0.95rem" }}>
                    Item list is empty
                  </p>
                  <p style={{ margin: "0 0 16px 0", color: "var(--slate-400)", fontSize: "0.82rem" }}>
                    Click below to select an available inventory item to sell.
                  </p>
                  <button
                    type="button"
                    onClick={handleAddRow}
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 6,
                      padding: "8px 18px",
                      borderRadius: 8,
                      border: "none",
                      backgroundColor: "var(--primary)",
                      color: "#fff",
                      fontWeight: 700,
                      fontSize: "0.84rem",
                      cursor: "pointer",
                    }}
                  >
                    <FiPlus size={15} /> Add Item to Sell
                  </button>
                </div>
              ) : (
                saleRows.map((row, index) => {
                const rowAvailableItems = [
                  ...(row.item_id && !availableItems.some((it) => String(it.id) === String(row.item_id))
                    ? [
                        {
                          id: row.item_id,
                          item_name: row.item_name,
                          sku: row.sku,
                          serial_number: row.serial_number,
                          part_number: row.part_number,
                          category_name: row.category_name,
                          subcategory_name: row.subcategory_name,
                          quantity: row.current_stock || row.quantity || 1,
                        },
                      ]
                    : []),
                  ...availableItems.filter(
                    (it) => String(it.id) === String(row.item_id) || !selectedItemIds.includes(String(it.id))
                  ),
                ];

                const priceVal = parseFloat(row.unit_price) || 0;
                const qtyVal = parseInt(row.quantity) || 1;
                const rowTotal = priceVal * qtyVal;

                return (
                  <div
                    key={row.rowId || index}
                    style={{
                      backgroundColor: row.item_id ? "#fff" : "#F8FAFC",
                      border: row.item_id ? "1.5px solid #BBF7D0" : "1.5px solid var(--slate-200)",
                      borderRadius: 10,
                      padding: "16px 20px",
                      boxShadow: "0 1px 3px rgba(0,0,0,0.03)",
                      transition: "all 0.15s ease",
                    }}
                  >
                    {/* Card Header: Item # and Add / Remove Actions */}
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        marginBottom: 14,
                        flexWrap: "wrap",
                        gap: 10,
                      }}
                    >
                      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                        <span
                          style={{
                            backgroundColor: row.item_id ? "var(--primary)" : "var(--slate-600)",
                            color: "#fff",
                            fontSize: "0.75rem",
                            fontWeight: 800,
                            padding: "3px 10px",
                            borderRadius: 12,
                          }}
                        >
                          Item #{index + 1}
                        </span>
                        {row.item_name && (
                          <span style={{ fontSize: "0.96rem", fontWeight: 800, color: "var(--navy)" }}>
                            {row.item_name}
                          </span>
                        )}
                      </div>

                      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        <button
                          type="button"
                          onClick={handleAddRow}
                          style={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 5,
                            padding: "5px 12px",
                            borderRadius: 6,
                            border: "1px solid var(--primary-light, #C7D2FE)",
                            backgroundColor: "var(--primary-light, #EEF2FF)",
                            color: "var(--primary, #4F46E5)",
                            fontSize: "0.78rem",
                            fontWeight: 700,
                            cursor: "pointer",
                          }}
                          title="Add row below"
                        >
                          <FiPlus size={13} /> Add
                        </button>

                        <button
                          type="button"
                          onClick={() => handleDeleteRow(index)}
                          style={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 5,
                            padding: "5px 12px",
                            borderRadius: 6,
                            border: "1px solid #FECACA",
                            backgroundColor: "#FEF2F2",
                            color: "var(--danger)",
                            fontSize: "0.78rem",
                            fontWeight: 700,
                            cursor: "pointer",
                          }}
                          title="Remove this row"
                        >
                          <FiTrash2 size={13} /> Remove
                        </button>
                      </div>
                    </div>

                    {/* Inputs Row: Searchable Item Select + Qty + Sale Price + Row Total */}
                    <div
                      style={{
                        display: "grid",
                        gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
                        gap: 16,
                        alignItems: "flex-start",
                      }}
                    >
                      {/* 1. Item Dropdown */}
                      <div style={{ gridColumn: "span 2", minWidth: 280 }}>
                        <label style={{ display: "block", fontSize: "0.82rem", fontWeight: 700, color: "var(--slate-700)", marginBottom: 6 }}>
                          Select Available Inventory Item *
                        </label>
                        <SearchableItemSelect
                          items={rowAvailableItems}
                          selectedItemId={row.item_id}
                          onSelect={(it) => handleItemSelectInRow(index, it)}
                        />
                      </div>

                      {/* 2. Count / Qty */}
                      <div style={{ minWidth: 120 }}>
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
                          <label style={{ fontSize: "0.82rem", fontWeight: 700, color: "var(--slate-700)" }}>
                            Count / Qty *
                          </label>
                          {row.item_id && (
                            <span
                              style={{
                                fontSize: "0.7rem",
                                fontWeight: 700,
                                backgroundColor: row.current_stock > 1 ? "#DCFCE7" : "#EFF6FF",
                                color: row.current_stock > 1 ? "#15803D" : "#1D4ED8",
                                padding: "1px 6px",
                                borderRadius: 4,
                              }}
                            >
                              Stock: {row.current_stock}
                            </span>
                          )}
                        </div>
                        <input
                          type="number"
                          min="1"
                          max={row.current_stock || 1}
                          step="1"
                          required
                          disabled={!row.item_id}
                          value={row.quantity || 1}
                          onChange={(e) => handleRowQuantityChange(index, e.target.value)}
                          style={{
                            width: "100%",
                            height: 42,
                            padding: "9px 12px",
                            borderRadius: 8,
                            border: "1.5px solid var(--slate-300)",
                            fontSize: "0.9rem",
                            fontWeight: 700,
                            color: "var(--navy)",
                            backgroundColor: row.item_id ? "#fff" : "var(--slate-100)",
                            boxSizing: "border-box",
                            textAlign: "center",
                          }}
                        />
                      </div>

                      {/* 3. Sale Price (Rs.) */}
                      <div style={{ minWidth: 150 }}>
                        <label style={{ display: "block", fontSize: "0.82rem", fontWeight: 700, color: "var(--slate-700)", marginBottom: 6 }}>
                          Unit Sale Price (Rs.) *
                        </label>
                        <div style={{ position: "relative" }}>
                          <span
                            style={{
                              position: "absolute",
                              left: 10,
                              top: "50%",
                              transform: "translateY(-50%)",
                              fontSize: "0.8rem",
                              fontWeight: 700,
                              color: "var(--slate-400)",
                              pointerEvents: "none",
                            }}
                          >
                            Rs.
                          </span>
                          <input
                            type="number"
                            step="0.01"
                            min="0"
                            placeholder="Enter Sale Price..."
                            required
                            disabled={!row.item_id}
                            value={row.unit_price}
                            onChange={(e) => handleRowPriceChange(index, e.target.value)}
                            style={{
                              width: "100%",
                              height: 42,
                              padding: "9px 12px 9px 36px",
                              borderRadius: 8,
                              border: !row.unit_price && row.item_id ? "1.5px solid #F59E0B" : "1.5px solid var(--slate-300)",
                              fontSize: "0.9rem",
                              fontWeight: 700,
                              color: "var(--navy)",
                              backgroundColor: row.item_id ? "#fff" : "var(--slate-100)",
                              boxSizing: "border-box",
                            }}
                          />
                        </div>
                      </div>

                      {/* 4. Total Amount (Rs.) */}
                      <div style={{ minWidth: 140 }}>
                        <label style={{ display: "block", fontSize: "0.82rem", fontWeight: 700, color: "var(--slate-700)", marginBottom: 6 }}>
                          Total (Rs.)
                        </label>
                        <div
                          style={{
                            height: 42,
                            padding: "9px 14px",
                            borderRadius: 8,
                            backgroundColor: "#F8FAFC",
                            border: "1.5px solid var(--slate-200)",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "flex-end",
                            boxSizing: "border-box",
                          }}
                        >
                          <span style={{ fontWeight: 800, color: "var(--navy)", fontSize: "0.92rem" }}>
                            Rs. {rowTotal.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Auto-populated details strip (Category, Serial Number, Part #, SKU) */}
                    {row.item_id && (
                      <div
                        style={{
                          marginTop: 14,
                          padding: "11px 16px",
                          backgroundColor: "#F8FAFC",
                          borderRadius: 8,
                          border: "1px dashed var(--slate-300)",
                          display: "flex",
                          alignItems: "center",
                          gap: 18,
                          flexWrap: "wrap",
                          fontSize: "0.82rem",
                        }}
                      >
                        <div>
                          <span style={{ color: "var(--slate-400)", textTransform: "uppercase", fontSize: "0.68rem", display: "block", fontWeight: 700 }}>
                            Category
                          </span>
                          <strong style={{ color: "var(--navy)" }}>
                            {row.category_name || "—"} {row.subcategory_name ? `• ${row.subcategory_name}` : ""}
                          </strong>
                        </div>

                        <div>
                          <span style={{ color: "var(--slate-400)", textTransform: "uppercase", fontSize: "0.68rem", display: "block", fontWeight: 700 }}>
                            Serial Number
                          </span>
                          {row.serial_number ? (
                            <span
                              style={{
                                fontFamily: "monospace",
                                fontWeight: 700,
                                color: "var(--navy)",
                                backgroundColor: "var(--slate-100)",
                                padding: "2px 6px",
                                borderRadius: 4,
                                fontSize: "0.8rem",
                              }}
                            >
                              {row.serial_number}
                            </span>
                          ) : (
                            <span style={{ color: "var(--slate-400)" }}>—</span>
                          )}
                        </div>

                        <div>
                          <span style={{ color: "var(--slate-400)", textTransform: "uppercase", fontSize: "0.68rem", display: "block", fontWeight: 700 }}>
                            Part Number
                          </span>
                          {row.part_number ? (
                            <span style={{ fontFamily: "monospace", color: "var(--slate-700)", fontWeight: 600, fontSize: "0.8rem" }}>
                              {row.part_number}
                            </span>
                          ) : (
                            <span style={{ color: "var(--slate-400)" }}>—</span>
                          )}
                        </div>

                        <div>
                          <span style={{ color: "var(--slate-400)", textTransform: "uppercase", fontSize: "0.68rem", display: "block", fontWeight: 700 }}>
                            SKU Number
                          </span>
                          <span
                            style={{
                              fontFamily: "monospace",
                              fontWeight: 700,
                              color: "var(--primary)",
                              backgroundColor: "var(--slate-100)",
                              padding: "2px 6px",
                              borderRadius: 4,
                              fontSize: "0.78rem",
                            }}
                          >
                            {row.sku || "—"}
                          </span>
                        </div>

                        <div>
                          <span style={{ color: "var(--slate-400)", textTransform: "uppercase", fontSize: "0.68rem", display: "block", fontWeight: 700 }}>
                            Status
                          </span>
                          <span
                            style={{
                              fontSize: "0.72rem",
                              backgroundColor: "#FEF3C7",
                              color: "#B45309",
                              padding: "2px 8px",
                              borderRadius: 4,
                              fontWeight: 700,
                            }}
                          >
                            Ready for Sale
                          </span>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })
            )}
            </div>

            {/* Total Item Count & Grand Total Amount Display */}
            <div
              style={{
                marginTop: 18,
                padding: "16px 20px",
                borderRadius: 8,
                backgroundColor: "#F0FDF4",
                border: "1.5px solid #BBF7D0",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                flexWrap: "wrap",
                gap: 16,
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: 20 }}>
                <div>
                  <span style={{ fontSize: "0.74rem", color: "#166534", fontWeight: 700, textTransform: "uppercase", display: "block" }}>
                    Total Items Selected
                  </span>
                  <strong style={{ fontSize: "1.25rem", color: "var(--navy)" }}>
                    {validItemsCount} {validItemsCount === 1 ? "Item" : "Items"}
                  </strong>
                </div>

                <div style={{ height: 32, width: 1, backgroundColor: "#86EFAC" }} />

                <div>
                  <span style={{ fontSize: "0.74rem", color: "#166534", fontWeight: 700, textTransform: "uppercase", display: "block" }}>
                    Grand Total Sale Amount
                  </span>
                  <strong style={{ fontSize: "1.4rem", color: "#15803D", fontWeight: 800 }}>
                    Rs. {grandTotalAmount.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </strong>
                </div>
              </div>

              {activeClient && (
                <div style={{ textAlign: "right" }}>
                  <span style={{ fontSize: "0.72rem", color: "#166534", fontWeight: 700, textTransform: "uppercase", display: "block" }}>
                    Selling To Client
                  </span>
                  <strong style={{ fontSize: "0.95rem", color: "var(--navy)" }}>
                    {activeClient.company_name}
                  </strong>
                </div>
              )}
            </div>
          </div>

          {/* Step 3: Complete Sale Action Bar */}
          <div
            style={{
              backgroundColor: "#fff",
              borderRadius: "var(--radius-lg)",
              border: "1px solid var(--slate-200)",
              padding: "16px 24px",
              boxShadow: "var(--shadow-sm)",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              flexWrap: "wrap",
              gap: 16,
              marginBottom: 40,
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <button
                type="button"
                onClick={handleResetForm}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 6,
                  padding: "9px 18px",
                  borderRadius: 8,
                  border: "1px solid var(--slate-300)",
                  backgroundColor: "#fff",
                  color: "var(--slate-600)",
                  fontWeight: 600,
                  fontSize: "0.86rem",
                  cursor: "pointer",
                }}
              >
                <FiRotateCcw size={15} /> Reset Form
              </button>
            </div>

            <button
              type="submit"
              disabled={submitting}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                padding: "11px 28px",
                borderRadius: 8,
                border: "none",
                backgroundColor: editingSale ? "#2563EB" : "var(--primary)",
                color: "#fff",
                fontWeight: 700,
                fontSize: "0.92rem",
                cursor: submitting ? "not-allowed" : "pointer",
                boxShadow: "0 2px 6px rgba(6, 78, 59, 0.25)",
              }}
            >
              <FiCheckCircle size={18} />
              {submitting
                ? (editingSale ? "Updating Invoice..." : "Processing Sale...")
                : (editingSale ? `Update Sale Invoice (#${editingSale.originalInvoiceNumber})` : "Complete Sale & Mark as Sold")}
            </button>
          </div>
        </form>
      )}

      {/* ========================================================================= */}
      {/* VIEW 2: SALE HISTORY & BREAKDOWN BY INVOICE NUMBER                        */}
      {/* ========================================================================= */}
      {activeTab === "history" && (
        <div className="fade-in">
          {/* Search Bar & Summary Stats */}
          <div
            style={{
              backgroundColor: "#fff",
              borderRadius: "var(--radius-lg)",
              padding: "16px 20px",
              border: "1px solid var(--slate-200)",
              boxShadow: "var(--shadow-sm)",
              marginBottom: 20,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              flexWrap: "wrap",
              gap: 16,
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: 10, flex: "1 1 340px", maxWidth: 540 }}>
              <div style={{ position: "relative", width: "100%" }}>
                <FiSearch
                  size={18}
                  style={{
                    position: "absolute",
                    left: 12,
                    top: "50%",
                    transform: "translateY(-50%)",
                    color: "var(--slate-400)",
                  }}
                />
                <input
                  type="text"
                  placeholder="Search by invoice #, client, item, serial, SKU..."
                  value={historySearch}
                  onChange={(e) => {
                    setHistorySearch(e.target.value);
                  }}
                  style={{
                    width: "100%",
                    padding: "9px 36px 9px 38px",
                    borderRadius: 8,
                    border: "1px solid var(--slate-300)",
                    fontSize: "0.88rem",
                    outline: "none",
                    boxSizing: "border-box",
                  }}
                />
                {historySearch && (
                  <button
                    type="button"
                    onClick={() => setHistorySearch("")}
                    style={{
                      position: "absolute",
                      right: 10,
                      top: "50%",
                      transform: "translateY(-50%)",
                      background: "none",
                      border: "none",
                      cursor: "pointer",
                      color: "var(--slate-400)",
                      padding: 2,
                      display: "flex",
                      alignItems: "center",
                    }}
                    title="Clear search"
                  >
                    <FiX size={15} />
                  </button>
                )}
              </div>

              <button
                type="button"
                onClick={() => fetchSaleHistory(historyPage, historyPageSize, debouncedHistorySearch)}
                disabled={historyLoading}
                style={{
                  height: 38,
                  padding: "0 14px",
                  borderRadius: 8,
                  border: "1px solid var(--slate-300)",
                  backgroundColor: "#fff",
                  color: "var(--slate-700)",
                  cursor: historyLoading ? "not-allowed" : "pointer",
                  display: "flex",
                  alignItems: "center",
                  gap: 6,
                  fontSize: "0.84rem",
                  fontWeight: 600,
                  flexShrink: 0,
                }}
                title="Refresh Sale History"
              >
                <FiRefreshCw size={14} style={{ animation: historyLoading ? "spin 1s linear infinite" : "none" }} />
                Refresh
              </button>
            </div>

            <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
              <div style={{ fontSize: "0.85rem", color: "var(--slate-500)", fontWeight: 600 }}>
                Showing {historyInvoices.length} of <strong style={{ color: "var(--navy)" }}>{historyPagination.total || 0}</strong> Invoices
                {debouncedHistorySearch && <span style={{ color: "var(--primary)", marginLeft: 4 }}>(filtered)</span>}
              </div>

              <button
                type="button"
                onClick={() => setActiveTab("record")}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 6,
                  backgroundColor: "var(--primary)",
                  color: "#fff",
                  padding: "8px 16px",
                  borderRadius: 8,
                  border: "none",
                  fontWeight: 700,
                  fontSize: "0.84rem",
                  cursor: "pointer",
                }}
              >
                <FiPlus size={15} /> Record New Sale
              </button>
            </div>
          </div>

          {/* Sale History Table: Grouped by Invoice Number */}
          <div
            style={{
              backgroundColor: "#fff",
              borderRadius: "var(--radius-lg)",
              border: "1px solid var(--slate-200)",
              boxShadow: "var(--shadow-sm)",
              overflow: "hidden",
            }}
          >
            <div style={{ overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "0.88rem" }}>
                <thead>
                  <tr
                    style={{
                      backgroundColor: "var(--slate-50)",
                      borderBottom: "1px solid var(--slate-200)",
                      textAlign: "left",
                    }}
                  >
                    <th style={{ padding: "12px 18px", color: "var(--slate-600)", fontWeight: 600, width: 50 }}>#</th>
                    <th style={{ padding: "12px 18px", color: "var(--slate-600)", fontWeight: 600 }}>Invoice #</th>
                    <th style={{ padding: "12px 18px", color: "var(--slate-600)", fontWeight: 600 }}>Client / Customer</th>
                    <th style={{ padding: "12px 18px", color: "var(--slate-600)", fontWeight: 600 }}>Sale Date</th>
                    <th style={{ padding: "12px 18px", color: "var(--slate-600)", fontWeight: 600 }}>Items Count</th>
                    <th style={{ padding: "12px 18px", color: "var(--slate-600)", fontWeight: 600 }}>Total Amount</th>
                    <th style={{ padding: "12px 18px", color: "var(--slate-600)", fontWeight: 600, textAlign: "right" }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {historyLoading ? (
                    <tr>
                      <td colSpan={7} style={{ padding: 45, textAlign: "center", color: "var(--slate-500)" }}>
                        <div style={{ display: "inline-flex", alignItems: "center", gap: 10, justifyContent: "center" }}>
                          <FiRefreshCw size={18} style={{ color: "var(--primary)", animation: "spin 1s linear infinite" }} />
                          <span style={{ fontWeight: 600, fontSize: "0.88rem" }}>Loading sale invoices from server...</span>
                        </div>
                      </td>
                    </tr>
                  ) : historyInvoices.length === 0 ? (
                    <tr>
                      <td colSpan={7} style={{ padding: 40, textAlign: "center", color: "var(--slate-400)" }}>
                        <div style={{ marginBottom: 10 }}>No sale invoice records found.</div>
                        <button
                          type="button"
                          onClick={() => setActiveTab("record")}
                          style={{
                            padding: "8px 16px",
                            backgroundColor: "var(--primary-light)",
                            color: "var(--primary)",
                            border: "none",
                            borderRadius: 8,
                            fontWeight: 700,
                            fontSize: "0.85rem",
                            cursor: "pointer",
                          }}
                        >
                          + Record First Sale
                        </button>
                      </td>
                    </tr>
                  ) : (
                    historyInvoices.map((inv, idx) => (
                      <tr
                        key={inv.invoice_number}
                        onClick={() => handleOpenInvoicePage(inv)}
                        style={{
                          borderBottom: "1px solid var(--slate-100)",
                          cursor: "pointer",
                          transition: "background 0.15s ease",
                        }}
                        onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "var(--slate-50)")}
                        onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "transparent")}
                        title="Click to open full printable invoice"
                      >
                        {/* Row Index */}
                        <td style={{ padding: "14px 18px", color: "var(--slate-400)", fontWeight: 600, fontSize: "0.82rem" }}>
                          {historyStartIndex + idx}
                        </td>

                        {/* Invoice Number */}
                        <td style={{ padding: "14px 18px" }}>
                          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                            <div
                              style={{
                                width: 28,
                                height: 28,
                                borderRadius: 6,
                                backgroundColor: "#EFF6FF",
                                color: "#2563EB",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                flexShrink: 0,
                              }}
                            >
                              <FiFileText size={15} />
                            </div>
                            <span
                              style={{
                                fontFamily: "monospace",
                                fontWeight: 800,
                                fontSize: "0.88rem",
                                color: "#1D4ED8",
                                backgroundColor: "#EFF6FF",
                                padding: "2px 8px",
                                borderRadius: 6,
                                border: "1px solid #DBEAFE",
                              }}
                            >
                              {inv.display_invoice_number}
                            </span>
                          </div>
                        </td>

                        {/* Client / Customer */}
                        <td style={{ padding: "14px 18px" }}>
                          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                            <div
                              style={{
                                width: 28,
                                height: 28,
                                borderRadius: 6,
                                backgroundColor: "#F5F3FF",
                                color: "#7C3AED",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                flexShrink: 0,
                              }}
                            >
                              <FiUsers size={14} />
                            </div>
                            <div>
                              <strong style={{ color: "var(--navy)", display: "block" }}>{inv.client_name}</strong>
                              {inv.client_contact_person && (
                                <span style={{ fontSize: "0.76rem", color: "var(--slate-500)" }}>
                                  Rep: {inv.client_contact_person}
                                </span>
                              )}
                            </div>
                          </div>
                        </td>

                        {/* Sale Date */}
                        <td style={{ padding: "14px 18px" }}>
                          <div style={{ display: "flex", alignItems: "center", gap: 6, color: "var(--slate-700)", fontWeight: 500 }}>
                            <FiCalendar size={14} style={{ color: "var(--slate-400)" }} />
                            {formatDateDisplay(inv.sale_date)}
                          </div>
                        </td>

                        {/* Items Count */}
                        <td style={{ padding: "14px 18px" }}>
                          <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
                            <span
                              style={{
                                display: "inline-flex",
                                alignItems: "center",
                                gap: 5,
                                padding: "3px 9px",
                                borderRadius: 12,
                                fontSize: "0.78rem",
                                fontWeight: 700,
                                backgroundColor: "var(--slate-100)",
                                color: "var(--slate-700)",
                              }}
                            >
                              <FiBox size={13} style={{ color: "var(--primary)" }} />
                              {inv.total_items ?? inv.quantity ?? inv.total_items_count ?? 0} {(inv.total_items ?? inv.quantity ?? inv.total_items_count) === 1 ? "Item" : "Items"}
                            </span>
                            {inv.returned_items > 0 && (
                              <span
                                style={{
                                  display: "inline-flex",
                                  alignItems: "center",
                                  gap: 4,
                                  padding: "2px 7px",
                                  borderRadius: 8,
                                  fontSize: "0.72rem",
                                  fontWeight: 700,
                                  backgroundColor: "#FEF2F2",
                                  color: "#DC2626",
                                  border: "1px solid #FEE2E2",
                                }}
                                title={`${inv.returned_items} item(s) returned`}
                              >
                                <FiRotateCcw size={11} /> -{inv.returned_items} Ret
                              </span>
                            )}
                          </div>
                        </td>

                        {/* Total Amount */}
                        <td style={{ padding: "14px 18px" }}>
                          <div style={{ fontWeight: 800, color: "#15803D", fontSize: "0.92rem" }}>
                            Rs. {(parseFloat(inv.total_price ?? inv.total_amount) || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                          </div>
                          {(parseFloat(inv.returned_amount ?? inv.total_returned_amount) || 0) > 0 && (
                            <div style={{ fontSize: "0.72rem", color: "#DC2626", fontWeight: 600, marginTop: 2 }}>
                              Ret: Rs. {(parseFloat(inv.returned_amount ?? inv.total_returned_amount) || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                            </div>
                          )}
                        </td>

                        {/* Action: View Invoice & Edit */}
                        <td style={{ padding: "14px 18px", textAlign: "right" }}>
                          <div style={{ display: "inline-flex", alignItems: "center", gap: 8, justifyContent: "flex-end" }}>
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleOpenInvoicePage(inv);
                              }}
                              style={{
                                display: "inline-flex",
                                alignItems: "center",
                                gap: 6,
                                padding: "6px 14px",
                                borderRadius: 6,
                                border: "1px solid var(--primary-light)",
                                backgroundColor: "var(--primary-light)",
                                color: "var(--primary)",
                                fontWeight: 700,
                                fontSize: "0.8rem",
                                cursor: "pointer",
                                transition: "all 0.15s ease",
                              }}
                            >
                              <FiEye size={14} /> View Invoice
                            </button>

                            {canEdit && (
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setSearchParams({ tab: "record", editInvoice: inv.invoice_number });
                                  loadSaleForEdit(inv.invoice_number || inv.id, inv);
                                }}
                                style={{
                                  display: "inline-flex",
                                  alignItems: "center",
                                  gap: 6,
                                  padding: "6px 12px",
                                  borderRadius: 6,
                                  border: "1px solid #93C5FD",
                                  backgroundColor: "#EFF6FF",
                                  color: "#1D4ED8",
                                  fontWeight: 700,
                                  fontSize: "0.8rem",
                                  cursor: "pointer",
                                  transition: "all 0.15s ease",
                                }}
                                title="Edit invoice details, client account, and prices"
                              >
                                <FiEdit size={14} /> Edit
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {/* Sale History Pagination Footer */}
            {historyPagination.total > 0 && (
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  padding: "12px 18px",
                  borderTop: "1px solid var(--slate-200)",
                  flexWrap: "wrap",
                  gap: 12,
                  backgroundColor: "var(--slate-50)",
                  fontSize: "0.84rem",
                  color: "var(--slate-600)",
                }}
              >
                <div>
                  Showing <strong>{historyStartIndex}</strong> to <strong>{historyEndIndex}</strong> of{" "}
                  <strong>{historyPagination.total}</strong> {historyPagination.total === 1 ? "invoice" : "invoices"}
                </div>

                <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
                  {/* Rows Per Page Selector */}
                  <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                    <span style={{ fontSize: "0.82rem", fontWeight: 600 }}>Rows:</span>
                    <select
                      value={historyPageSize}
                      onChange={(e) => {
                        setHistoryPageSize(Number(e.target.value));
                        setHistoryPage(1);
                      }}
                      style={{
                        padding: "4px 8px",
                        borderRadius: 6,
                        border: "1px solid var(--slate-300)",
                        fontSize: "0.82rem",
                        backgroundColor: "#fff",
                        fontWeight: 600,
                        color: "var(--navy)",
                        cursor: "pointer",
                      }}
                    >
                      <option value={5}>5</option>
                      <option value={10}>10</option>
                      <option value={20}>20</option>
                      <option value={50}>50</option>
                      <option value={100}>100</option>
                    </select>
                  </div>

                  {/* Prev Button */}
                  <button
                    type="button"
                    disabled={historyPage <= 1 || historyLoading}
                    onClick={() => setHistoryPage((p) => Math.max(p - 1, 1))}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 4,
                      padding: "5px 12px",
                      borderRadius: 6,
                      border: "1px solid var(--slate-300)",
                      backgroundColor: historyPage <= 1 ? "var(--slate-100)" : "#fff",
                      color: historyPage <= 1 ? "var(--slate-400)" : "var(--slate-700)",
                      cursor: historyPage <= 1 || historyLoading ? "not-allowed" : "pointer",
                      fontWeight: 600,
                      fontSize: "0.82rem",
                      transition: "all 0.15s ease",
                    }}
                  >
                    <FiChevronLeft size={15} /> Prev
                  </button>

                  {/* Numbered Page Buttons */}
                  <div style={{ display: "flex", gap: 4, alignItems: "center" }}>
                    {Array.from({ length: totalHistoryPages }, (_, i) => i + 1)
                      .filter((p) => {
                        if (totalHistoryPages <= 7) return true;
                        if (p === 1 || p === totalHistoryPages) return true;
                        if (Math.abs(p - historyPage) <= 1) return true;
                        return false;
                      })
                      .map((p, idx, arr) => {
                        const prev = arr[idx - 1];
                        const showEllipsis = prev && p - prev > 1;

                        return (
                          <React.Fragment key={p}>
                            {showEllipsis && (
                              <span style={{ padding: "0 4px", color: "var(--slate-400)" }}>...</span>
                            )}
                            <button
                              type="button"
                              disabled={historyLoading}
                              onClick={() => setHistoryPage(p)}
                              style={{
                                width: 28,
                                height: 28,
                                borderRadius: 6,
                                border: p === historyPage ? "1.5px solid var(--primary)" : "1px solid var(--slate-300)",
                                backgroundColor: p === historyPage ? "var(--primary)" : "#fff",
                                color: p === historyPage ? "#fff" : "var(--slate-700)",
                                fontWeight: 700,
                                fontSize: "0.8rem",
                                cursor: "pointer",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                              }}
                            >
                              {p}
                            </button>
                          </React.Fragment>
                        );
                      })}
                  </div>

                  {/* Next Button */}
                  <button
                    type="button"
                    disabled={historyPage >= totalHistoryPages || historyLoading}
                    onClick={() => setHistoryPage((p) => Math.min(totalHistoryPages, p + 1))}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 4,
                      padding: "5px 12px",
                      borderRadius: 6,
                      border: "1px solid var(--slate-300)",
                      backgroundColor: historyPage >= totalHistoryPages ? "var(--slate-100)" : "#fff",
                      color: historyPage >= totalHistoryPages ? "var(--slate-400)" : "var(--slate-700)",
                      cursor: historyPage >= totalHistoryPages || historyLoading ? "not-allowed" : "pointer",
                      fontWeight: 600,
                      fontSize: "0.82rem",
                      transition: "all 0.15s ease",
                    }}
                  >
                    Next <FiChevronRight size={15} />
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ========================================================================= */}
      {/* VIEW 3: SALE RETURNS                                                      */}
      {/* ========================================================================= */}
      {activeTab === "returns" && (
        <SaleReturnsTab clients={clients} sales={sales} onReturnChange={() => fetchData(true)} />
      )}



      {/* Delete Confirmation Modal */}
      <Modal isOpen={deleteModal.open} onClose={() => setDeleteModal({ open: false, sale: null })} maxWidth={400}>
        <div style={{ textAlign: "center" }}>
          <div
            style={{
              width: 48,
              height: 48,
              borderRadius: "50%",
              backgroundColor: "var(--danger-bg)",
              color: "var(--danger)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              margin: "0 auto 16px",
            }}
          >
            <FiTrash2 size={24} />
          </div>
          <h4 style={{ fontSize: "1.1rem", fontWeight: 700, color: "var(--navy)", marginBottom: 8 }}>
            Delete Sale Record
          </h4>
          <p style={{ fontSize: "0.85rem", color: "var(--slate-500)", marginBottom: 20 }}>
            Are you sure you want to delete this sale record for{" "}
            <strong>"{deleteModal.sale?.item_name}"</strong> sold to{" "}
            <strong>"{deleteModal.sale?.client_name}"</strong>?
          </p>
          <div style={{ display: "flex", justifyContent: "center", gap: 12 }}>
            <button
              type="button"
              onClick={() => setDeleteModal({ open: false, sale: null })}
              style={{
                padding: "8px 18px",
                borderRadius: "var(--radius-md)",
                border: "1px solid var(--slate-300)",
                backgroundColor: "#fff",
                cursor: "pointer",
                fontWeight: 600,
              }}
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleDeleteSale}
              style={{
                padding: "8px 20px",
                borderRadius: "var(--radius-md)",
                border: "none",
                backgroundColor: "var(--danger)",
                color: "#fff",
                cursor: "pointer",
                fontWeight: 700,
              }}
            >
              Delete Sale
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default SalesPage;
